/**
 * Frame graph (PRD §25):
 *
 *   particle compute → HDR scene (MSAA, resolved) → bright → blur ×4
 *     → underwater composite (volumetrics, distortion, bloom, grade) → swapchain
 *
 * Everything in the scene pass is instanced or chunked; there are no per-object
 * uniform buffers and no buffer recreation per frame.
 */

import sceneCode from './shaders/scene.wgsl?raw';
import { buildEnvTexture, ENV_BANDS, ENV_LAYERS, ENV_MIPS, ENV_SH_ALL } from './envMap';
import { buildStudioTexture, STUDIO_MIP_MAX } from './studioMap';
import postCode from './shaders/post.wgsl?raw';
import { checkShader, makeUniform, type Gpu } from './gpu';
import { TextBatch } from './text';
import { CAMERA, LIGHT, SEA } from '../game/config';

/**
 * The sun intensity the sky was authored at, and the reference the WATER's sun
 * is normalised against.
 *
 * `skyTune.sunIntensity` drove the atmosphere and nothing else, so turning the
 * sun up brightened the sky above the waterline and left everything under it
 * exactly as dark — which is not a sun, it is a backdrop. `sunStrength` now
 * carries the ratio to this number, so the slider means the same thing on both
 * sides of the surface and means NOTHING at its default, where the ratio is 1.
 */
export const SKY_SUN_REF = 4.2;

/** The gameplay plane's height in metres at zoom 1. See the mote box below. */
const CAMERA_VIEW_HEIGHT = CAMERA.viewHeight;
import {
  MESH_VERTEX_LAYOUT, TERRAIN_VERTEX_LAYOUT, VERT_FLOATS, meshHalfExtents, uploadMesh,
  type GpuMesh, type MeshData,
} from './mesh';
import { Particles } from './particles';
import { maskRect } from '../core/math';
import { Fluid } from './fluid';
import { LAYERS, type Chunk } from '../game/terrain';
import { AO_EXPONENT, layerVisibility } from '../game/mesher';
import { mat4, orthoTopDown, type Mat4 } from '../core/math';
import type { Quality } from '../core/quality';
import type { DetailTextures } from './textures';


/**
 * Fraction of the tier's volumetric march to run, and the gain on its result.
 *
 * Both are 1: the march is the searchlight, and the searchlight is the hero
 * effect of the whole game. A shorter march was tried at 0.4 and read as more
 * pronounced in open water — fewer, longer samples give a chunkier column of
 * light — but on the beam itself, in a dark cave, the same chunkiness is
 * banding, and that is where the lamp actually matters. It is not a trade worth
 * a few milliseconds.
 *
 * The gain is a separate constant rather than a division by the scale, which is
 * what it used to be. `dtStep` is already `distance / steps`, so the sum times
 * dtStep is a Riemann integral and independent of the step count — dividing by
 * the scale on top of that was a hidden brightness boost masquerading as a
 * correction. Keeping them apart means the step count is a pure performance
 * dial: F4 changes the cost and cannot change the exposure.
 */
const VOLUMETRIC_SCALE = 1;
const VOL_GAIN = 1;

const HDR_FORMAT: GPUTextureFormat = 'rgba16float';

/**
 * What each quality step costs, in samples. OFF, LOW, MEDIUM, HIGH, ULTRA.
 *
 * These are the only dial that matters for the frame: everything in the
 * composite is a march, and a march costs exactly its step count. HIGH is what
 * the effects were tuned at, so it is the default and the reference — LOW and
 * MEDIUM are there to be measured against it rather than to be lived with.
 */
const AO_TAPS = [0, 4, 6, 8, 14];
const SHADOW_STEPS = [0, 3, 4, 6, 10];
const SSR_STEPS = [0, 4, 7, 10, 16];
/**
 * Caustic map, in texels along one edge, over the 128 m window `CAUSTIC_HALF`
 * declares in post.wgsl. 256 is half a metre a texel, which is the width of a
 * filament — finer than that and the splat count has to rise to fill it.
 */
const CAUSTIC_MAP_X = 512;
const CAUSTIC_MAP_Z = 256;
/**
 * The height map's world rectangle. MUST match CAUSTIC_HALF_X/Z and
 * CAUSTIC_PUSH in post.wgsl — the two share a UV mapping, which is the whole
 * reason a lookup into one costs nothing extra in the other.
 */
/**
 * TWICE AS WIDE AS IT WAS, AND THE TEXTURE WITH IT — 512 x 256 rather than
 * 256 x 256, so a texel is still half a metre in x and one metre in z.
 *
 * 64 was never enough, and the window lead only exposed it. The camera can see
 * 58 m either side at full zoom-out on a wide monitor, so ±64 covered the frame
 * with six metres to spare — and then the lead started sliding that window up
 * to 64 m up-sun to keep deep shafts on the map, which took the whole down-sun
 * half of the frame off it. Ground caustics on one side of the screen and none
 * on the other, exactly as reported.
 *
 * 128 covers the widest frame (58) plus the largest lead (64) with room left.
 * Both consumers get what they need out of one map and the trade disappears
 * instead of being tuned.
 *
 * The splat grid stays at 1024 x 1024, so the same million points now cover
 * twice the area: 8 per texel where there were 16. That is the cost, and it
 * buys a uniform 0.25 m spacing in both axes where it used to be 0.125 by 0.25.
 * If the pattern ever reads grainy, `CAUSTIC_GRID` is the dial.
 */
const HEIGHT_HALF_X = 128;
const HEIGHT_HALF_Z = 128;
const HEIGHT_PUSH = 64;
/** The slab of world the map can record, relative to the water surface. */
const HEIGHT_TOP = 90;
const HEIGHT_BOTTOM = -110;
/**
 * Splat grid along one edge, squared. 1024 puts sixteen points in every texel
 * of a 256 map — the count IS the signal now, so it is also the noise floor.
 * Must match CAUSTIC_GRID in post.wgsl.
 */
const CAUSTIC_POINTS = 1024 * 1024;
/** Cloud octaves. Only ever paid above the waterline. */
const SKY_OCTAVES = [0, 1, 2, 3, 4];
const DEPTH_FORMAT: GPUTextureFormat = 'depth32float';
const INSTANCE_FLOATS = 52; // model + prevModel + tint + emissive + params + params2 + pbr
const MOTION_FORMAT = 'rg16float';
/**
 * The material G-buffer: R metalness, G roughness, B reflectivity, A glass.
 *
 * The composite runs AFTER the scene pass and had no idea what any pixel was
 * made of, so screen-space reflections could only ever apply one Fresnel curve
 * to everything and glass could not refract at all. Eight bits a channel is
 * ample — these are shading weights, not colour.
 */
const MRF_FORMAT: GPUTextureFormat = 'rgba8unorm';
/**
 * Storage the light buffer is ALLOCATED for. What a frame may actually use is
 * `lightCap`, set from the quality tier. 192 lights of 8 floats is 6 KB, so
 * sizing for the ceiling and capping at runtime costs nothing measurable.
 */
const MAX_LIGHTS = 192;

export interface DrawItem {
  model: Mat4;
  tint: [number, number, number, number];
  emissive: [number, number, number, number];
}

interface MeshSlot {
  mesh: GpuMesh;
  capacity: number;
  data: Float32Array<ArrayBuffer>;
  /** Last frame's model matrices, by instance index. See `queue`. */
  prev: Float32Array<ArrayBuffer>;
  buffer: GPUBuffer;
  bind: GPUBindGroup;
  count: number;
  /** Translucent slots draw after the opaque pass, with depth write off. */
  translucent: boolean;
  /**
   * A translucent slot that is GLASS rather than tissue: same pass and the same
   * blend, but `vsGlass`/`fsGlass` instead of the jellyfish pair — rigid rather
   * than swimming, and Fresnel rather than a membrane. See `fsGlass`.
   */
  glass: boolean;
  /** Mesh-local bounds used to conservatively cover refractive fragments. */
  refractHalf: [number, number, number];
  /** Largest |uv.y|; vertex animation amplitudes are functions of this value. */
  refractMaxV: number;
}

/** Pixel bounds with an exclusive high edge, matching texture copy extents. */
interface PixelRect {
  x0: number; y0: number; x1: number; y1: number;
}

const BACKDROP_COPY_ALIGNMENT = 16;
const MAX_BACKDROP_REGIONS = 8;

const rectArea = (r: PixelRect) => (r.x1 - r.x0) * (r.y1 - r.y0);
const rectUnion = (a: PixelRect, b: PixelRect): PixelRect => ({
  x0: Math.min(a.x0, b.x0), y0: Math.min(a.y0, b.y0),
  x1: Math.max(a.x1, b.x1), y1: Math.max(a.y1, b.y1),
});
const rectTouches = (a: PixelRect, b: PixelRect) =>
  a.x0 <= b.x1 && b.x0 <= a.x1 && a.y0 <= b.y1 && b.y0 <= a.y1;

/** Insert a rectangle while keeping the list free of overlapping regions. */
function addBackdropRegion(rects: PixelRect[], rect: PixelRect) {
  for (let i = 0; i < rects.length;) {
    if (!rectTouches(rects[i]!, rect)) { i++; continue; }
    rect = rectUnion(rects[i]!, rect);
    rects.splice(i, 1);
    // The larger union may now touch something inspected earlier.
    i = 0;
  }
  rects.push(rect);
}

/**
 * Bound command count without dropping coverage. Merge the pair whose bounding
 * rectangle introduces the least extra copied area until the cap is reached.
 */
function capBackdropRegions(rects: PixelRect[]) {
  while (rects.length > MAX_BACKDROP_REGIONS) {
    let ai = 0, bi = 1, best = Infinity;
    for (let i = 0; i < rects.length - 1; i++) {
      for (let j = i + 1; j < rects.length; j++) {
        const extra = rectArea(rectUnion(rects[i]!, rects[j]!))
          - rectArea(rects[i]!) - rectArea(rects[j]!);
        if (extra < best) { best = extra; ai = i; bi = j; }
      }
    }
    const merged = rectUnion(rects[ai]!, rects[bi]!);
    rects.splice(bi, 1);
    rects.splice(ai, 1);
    addBackdropRegion(rects, merged);
  }
}

export interface FrameParams {
  viewProj: Mat4;
  /** The same, minus the temporal jitter. Motion vectors are measured on this. */
  viewProjNJ: Mat4;
  /** Seconds since the previous frame, for velocity-based reprojection. */
  dt: number;
  invViewProj: Mat4;
  camX: number; camY: number; camZ: number;
  /** The z plane the camera is focused on — the lane the sub is flying in. */
  focusZ: number;
  /**
   * Camera distance to the play plane, and the player's magnification.
   *
   * Both feed depth of field, which needs a focal PLANE and a focal LENGTH and
   * previously had neither — see the note in post.wgsl on `cocAt`.
   */
  focusDist: number;
  viewZoom: number;
  /**
   * Tangents of the angles from straight-ahead to the top and bottom of the
   * frustum, tilt included. Two numbers rather than one half-angle because a
   * tilted camera does not see an equal amount above and below the point it is
   * aimed at: the ground below recedes toward a horizon and the visible span
   * stretches downward, while the top edge pulls in.
   */
  tanTop: number;
  tanBot: number;
  time: number;
  sunX: number; sunY: number; sunZ: number; sunStrength: number;
  /** The sun before the surface refracts it. The sky reads this one. */
  skyX: number; skyY: number; skyZ: number;
  /**
   * sin(elevation) of the time-of-day PRESET the sun came from, before the
   * elevation slider moved it. `-skyY` is where the sun is now; this is where
   * the preset's authored colour was chosen for. See `sunTint`.
   */
  sunRefY: number;
  sunR: number; sunG: number; sunB: number;
  /** 0 keeps the sea edge-on; 1 draws it as a plane to the horizon. */
  planeView: number;
  /** 0 day, 1 night. Brings the stars out. */
  night: number;
  fog: [number, number, number];
  ambientLevel: number;
  ext: [number, number, number];
  surfaceY: number;
  /**
   * How much sky the camera can see, 0 to 1.
   *
   * The gate that makes the whole thing free at depth. Below the surface by
   * more than a few metres this is zero, and the composite skips the sky
   * outright — not a cheaper sky, no sky at all: no ray, no clouds, no sun. At
   * four thousand metres it costs one comparison per pixel that had no
   * geometry, which is a branch the shader was taking anyway.
   */
  skyAmount: number;
  /** World Z of the plane the game is played in — the sea is read there. */
  planeZ: number;
  ambient: [number, number, number];
  normDepth: number;
  lightX: number; lightY: number; lightZ: number; lightRange: number;
  lightDX: number; lightDY: number; lightDZ: number; lightInner: number;
  lightR: number; lightG: number; lightB: number; lightOuter: number;
  lightIntensity: number;
  /** Where the propeller wash goes in, and how hard. */
  wakeX: number; wakeY: number; wakeZ: number;
  /** The boat in screen uv, and the radius of clear water to leave round it. */
  wakeSubU: number; wakeSubV: number; wakeGap: number;
  /** 0..1 burner blend. The wake goes violet and violent with it. */
  wakeNitro: number;
  wakeVX: number; wakeVY: number; wakeThrust: number;
  sonarX: number; sonarY: number; sonarRadius: number; sonarStrength: number;
  coneAngle: number; coneHalf: number; coneBins: number;
  exposure: number;
  distortion: number;
  vignette: number;
  bloom: number;
  /**
   * Confine bloom to one rectangle of the frame, in view FRACTIONS — centre
   * u, v with width and height wf, hf, the same shape `showcase` uses. Null
   * blooms the whole frame.
   *
   * For the refit panel, and the reason it is a confinement rather than a
   * switch is that both directions across that border are wrong. Bloom is a
   * blur of the whole HDR frame taken before the HUD scrims anything, so the
   * lit station OUTSIDE the panel smears across the border and lands on the
   * black card — which is what the blue smudges in the corners of the viewport
   * were. And a crystal that glows inside the panel would smear back out over
   * the rows.
   *
   * Confining it answers both at once: nothing outside the rect contributes,
   * and nothing inside it escapes.
   */
  bloomRect: { u: number; v: number; wf: number; hf: number } | null;
  damageFlash: number;
}

/**
 * Timestamps allocated per frame: one pair for the whole frame plus one per
 * named pass.
 *
 * 64 is well clear of the ~20 passes a frame can encode and nowhere near the
 * 4096 a query set is allowed. Allocation is per frame from a counter, so a
 * frame that skips bloom or depth of field simply uses fewer — which is what
 * makes the labels have to travel with the readback rather than being a fixed
 * table: the pass LIST changes between frames, and the numbers come back two
 * frames after the list that produced them.
 */
const TS_QUERIES = 64;

export class Renderer {
  readonly particles: Particles;
  /** The wake. Simulated in world space; the composite refracts through it. */
  private fluid!: Fluid;
  readonly device: GPUDevice;

  private hdrMS!: GPUTexture;
  private hdr!: GPUTexture;
  private depth!: GPUTexture;
  private bloomA!: GPUTexture;
  private bloomB!: GPUTexture;
  /** Half-resolution inscatter. RGB is the light, alpha the depth it marched to. */
  private volTex!: GPUTexture;
  private vVol!: GPUTextureView;
  private volW = 1;
  private volH = 1;
  private volPipe!: GPURenderPipeline;
  private volBind!: GPUBindGroup;
  /** Half-resolution defocus copy of the scene. Two, to blur separably. */
  private dofB!: GPUTexture;
  private vDofB!: GPUTextureView;
  private dofPipe!: GPURenderPipeline;
  private dofBind!: GPUBindGroup;
  /**
   * When FXAA is on the composite lands here instead of the swapchain, and the
   * edge pass reads it. Same format, so nothing else in the chain changes.
   */
  private readonly fxaa: boolean;
  private readonly taa: boolean;
  /**
   * Sub-pixel offset for this frame, in NDC. Read by the camera before it
   * builds its matrices.
   */
  jitterX = 0;
  jitterY = 0;
  private jitterIx = 0;
  /** Motion vectors, and the two colour buffers the resolve ping-pongs. */
  private motion?: GPUTexture;
  private vMotion!: GPUTextureView;
  private mrf!: GPUTexture;
  private mrfMS!: GPUTexture;
  private vMrf!: GPUTextureView;
  private vMrfMS!: GPUTextureView;
  private history: GPUTexture[] = [];
  private vHistory: GPUTextureView[] = [];
  private taaPipe!: GPURenderPipeline;
  private taaBinds: GPUBindGroup[] = [];
  private taaFlip = 0;
  /** True until the first frame has been resolved; there is no history yet. */
  private taaCold = true;
  private prevVP = new Float32Array(16);
  private ldr?: GPUTexture;
  private vLdr!: GPUTextureView;
  private fxaaPipe!: GPURenderPipeline;
  private sharpenPipe!: GPURenderPipeline;
  /**
   * One per history parity. The sharpen reads the RESOLVED frame, which is
   * whichever history buffer TAA just wrote, so it needs the same pair of
   * bindings TAA flips between.
   */
  private sharpenBinds: (GPUBindGroup | null)[] = [null, null];
  private fxaaBind!: GPUBindGroup;
  /**
   * Aperture, as an f-number. 0 is off.
   *
   * Blur scales as one over this, the way a real lens does: the entrance pupil
   * is the focal length divided by the f-number, and the circle a point source
   * spreads into is proportional to it. So f/1.2 is five times the blur of
   * f/12, and the numbers on the dial mean what a photographer expects.
   */
  dof = 0;
  /** Screen-space AO, shadows, reflections and refraction. See Settings.ssfx. */
  ssfx = true;
  /**
   * The sea surface, as an ABLATION SWITCH rather than a quality tier.
   *
   * `planeView` is camera state — how far the eye is above the waterline — so
   * there was no way to turn the surface off to see what it costs, and the sky
   * dial does not touch it. The F7 ladder needs them separated: the sky is a
   * flat gradient and some clouds, the sea is a per-pixel ray march into a
   * displaced height field, and they were being measured as one thing.
   */
  seaSurface = true;
  /** Light the hull from the baked environment rather than the analytic studio. */
  ibl = true;
  /** How hard the final pass sharpens. 0 is off. */
  sharpness = 0.55;
  /** Player exposure multiplier. 1 is what the depth bands were tuned at. */
  brightness = 1;
  /** Quality of each screen-space effect, 0 off to 4 ultra. */
  gfxAo = 3;
  gfxShadow = 3;
  /**
   * ON, at 7 steps.
   *
   * It was opt-in, and the reasoning was sound for the world it was written in:
   * "at a grazing water view this marched most of the screen to reflect a
   * handful of objects that the broad sky/body reflection already describes."
   * That world had no large hard-surfaced structures in it. A habitat is
   * exactly the case the broad reflection cannot describe — a metal panel two
   * metres from a lit cyan strip should show the strip, and the baked probe has
   * no idea the strip exists.
   *
   * This is also the honest answer to "we should have per-location IBL probes":
   * for a nearby object, screen-space reflection IS the local probe, and it is
   * a better one because it updates. Probes would still be worth having for
   * what is off screen; this covers what is on it.
   */
  gfxSsr = 2;
  /** Sky detail above the waterline, 0 off to 4 ultra. */
  gfxSky = 3;
  private bloomW = 1;
  private bloomH = 1;
  private vHdrMS!: GPUTextureView;
  private vHdr!: GPUTextureView;
  private backdrop!: GPUTexture;
  private vBackdrop!: GPUTextureView;
  /** Group 2 on the translucent pipeline — see `fsJelly`'s refraction. */
  private backdropBind!: GPUBindGroup;
  private backdropLayout!: GPUBindGroupLayout;
  private vDepth!: GPUTextureView;
  private vBloomA!: GPUTextureView;
  private vBloomB!: GPUTextureView;
  /**
   * The world-space caustic map. Fixed size, not tied to the render resolution:
   * it covers a fixed number of METRES, so its texel density is a property of
   * the world rather than of the window.
   */
  private height!: GPUTexture;
  private vHeight!: GPUTextureView;
  /**
   * A group of ONE for the height pass, and it has to be its own.
   *
   * `globalsBind` now carries the height map at binding 8, and the height pass
   * WRITES that texture — binding a texture for reading in the same pass that
   * renders to it is invalid, which is exactly what the device said:
   *
   *   usage (TextureBinding|RenderAttachment) includes writable usage and
   *   another usage in the same synchronization scope
   *
   * The depth-only vertex shaders need `G` and nothing else, so the smallest
   * possible group is also the correct one.
   */
  private heightBind!: GPUBindGroup;
  private heightTerrainPipe!: GPURenderPipeline;
  private heightMeshPipe!: GPURenderPipeline;
  private caustic!: GPUTexture;
  private vCaustic!: GPUTextureView;
  private causticPipe!: GPURenderPipeline;
  private causticBind!: GPUBindGroup;

  private globalsBuf: GPUBuffer;
  private lightsBuf: GPUBuffer;
  private lightCountBuf: GPUBuffer;
  private scatterLightsBuf: GPUBuffer;
  private scatterLightCountBuf: GPUBuffer;
  private postBuf: GPUBuffer;
  private blurBufs: GPUBuffer[] = [];

  private globalsData: Float32Array<ArrayBuffer> = new Float32Array(160);
  /** Scratch for the height map's projection, rebuilt once a frame. */
  private heightVP: Mat4 = mat4();
  /** base.x, base.z, span.x, span.z of the caustic map, in world metres. */
  private readonly causticWin = new Float32Array(4);
  /**
   * How far that window leads up-sun, metres. The HEIGHT map does not lead —
   * what stands over a piece of water has nothing to do with where the sun is
   * — so the shader needs this to recover one rectangle from the other.
   */
  private causticLead = 0;
  /** The height map's depth slab, in world y. Uploaded as `tune9.xy`. */
  private slabTop = 12;
  private slabBot = -520;

  /** How much of the sky the deck covers, 0..1. */
  cloudCover = 0.685;
  /**
   * LIVE SKY TUNING. Every constant the sky argument has been about, moved out
   * of the shader so it can be changed while looking at the result rather than
   * edited, rebuilt and screenshotted. These defaults ARE the values they
   * replaced, so an untouched game renders exactly as it did.
   */
  skyTune = {
    /** Per metre at sea level. */
    density: 3.233e-5,
    /** Metres. Low means the air near the surface scatters hard. */
    scaleHeight: 500,
    /**
     * How hard the atmosphere reddens the sun as it drops toward the horizon.
     *
     * NEUTRAL AT ZENITH by construction — see `sunTint` — so this cannot change
     * a midday frame at any value, and 0 switches it off entirely.
     *
     * It needs a multiplier of its own because `density * rayleigh *
     * scaleHeight` here is 0.017 against a real atmosphere's ~0.25 for blue at
     * zenith. Those numbers were chosen so the SCATTERED sky looks right, and
     * the scattered term reads `1 - T` where this reads `T`; the same
     * constants that make a good sky make almost no sunset. At 12 the sun is
     * white overhead, warm by 20 degrees, orange at 12, deep orange at 7 and
     * red in the last three — measured, not eyeballed.
     */
    sunset: 12,
    /**
     * Whether the sunset tint covers the whole sky or only the horizon.
     *
     * 0 tints the dome uniformly. That is not physical — the sun's path to a
     * scattering point high in the air is far shorter than to one at sea level
     * — but with the sun a degree above the water it paints the entire sky, and
     * that is a picture worth being able to choose.
     *
     * 1 weights it by how low the view ray scatters, so the horizon takes the
     * whole tint and the zenith keeps its blue. Physical, and it is what stops
     * a sun at eight degrees turning the sky olive rather than golden.
     *
     * Defaults to 0 because that is what the sky did when this was first
     * switched on, and changing it silently would re-grade a look that was
     * already being tuned against.
     */
    sunsetGradient: 0,
    rayleigh: 1.047,
    mie: 0.183,
    mieMul: 0.486,
    /** Forward bias of the Mie lobe. High is a tight halo round the sun. */
    mieG: 0.5823,
    /** Ceiling on optical depth. Without one every channel saturates to grey. */
    odCap: 33500,
    sunIntensity: SKY_SUN_REF,
    /** Strength of the polariser ninety degrees off the sun. */
    polarise: 1.09,
    cloudAlt: 2600,
    mistNear: 90,
    mistFar: 340,
    /**
     * Sun position, as an OFFSET from whichever time of day is selected, in
     * degrees. Zero is the preset exactly.
     *
     * Azimuth is the one that matters. The level runs along x and the camera
     * looks down -z, so a preset at 180 puts a low sun ninety degrees off the
     * view axis and out of frame however low it gets. Every azimuth is equally
     * physical — it is the compass bearing of the coastline — so which one to
     * ship is a look decision and belongs on a control.
     */
    sunAzimuth: 0,
    sunElevation: 0,
    /**
     * Render resolution, in MILLIONS OF PIXELS, live.
     *
     * The one number the effect ablation cannot reach. With every effect off the
     * GPU is still nearly saturated, which leaves the fixed chain — a
     * multi-target scene pass, a composite, a temporal resolve, a sharpen and an
     * FXAA, each reading and writing the whole frame. That cost is bandwidth,
     * and bandwidth scales with PIXELS rather than with what the shader does to
     * each one. Dragging this is the experiment that separates the two.
     */
    pixels: 2.4,
    /**
     * The HUD overlay's own budget, in megapixels. It is a separate 2D canvas
     * composited over the render, it was uncapped, and at `devicePixelRatio` 2
     * it was three times the size of the 3D frame underneath it.
     */
    hudPixels: 4.0,
  };
  /**
   * The surface and what it throws on the seabed. Same arrangement as
   * `skyTune`: read into the uniform every frame, so a slider is live.
   */
  seaTune = {
    /** How readily a steep wave breaks into white. */
    foam: 0.08,
    /** Scale of the cell noise the froth is made of. */
    frothScale: 0.86,
    /** The sun's own highlight off the wave facets. */
    glint: 2.35,
    /** How much of the scene the surface reflects back. */
    reflection: 4.158,
    /** Brightness of the light the surface focuses onto the seabed. */
    caustic: 0.12,
    /**
     * FOCUS DEPTH, in metres: how far below the surface the splat pass projects
     * its rays. Sets the size of the filaments and the speed they move at, and
     * those are one quantity rather than two — both are the ray's lateral throw,
     * which is proportional to how far it travelled.
     */
    causticSize: 33.01,
    /** How deep the caustics still reach, in metres. */
    causticReach: 345,
    /**
     * The hard bright middle of a sun shaft, on top of the broad soft one.
     * The four depth-spread taps give a single edge profile; a real refracted
     * beam has a narrow core where the focusing still holds inside a wide
     * scattered envelope. 0 is the beam exactly as it was.
     */
    shaftCore: 0.55,
    /**
     * Light and dark travelling down a shaft's length, from the same surface,
     * sampled with a depth-proportional shear ACROSS the sun. 0 is the smooth
     * cylinder.
     */
    shaftGrain: 0.5,
    /**
     * How far the caustic window leads UP-SUN, as a fraction of the throw at
     * the camera's own depth. 0 is the camera-centred window the shafts were
     * falling off the right-hand edge of.
     */
    shaftLead: 1,
    /**
     * The studio softbox that reflective things fall back to when screen space
     * has nothing for them. See `studioMap.ts`. 0 is a mirror reflecting the
     * void behind the camera, which is what chrome did before this existed.
     */
    studio: 0.198,
    /**
     * Light bouncing UP off the seabed onto the undersides of things.
     *
     * Down here the floor is the brightest surface in the frame and the whole
     * column between is scattering, so a boulder's underside cannot be dark.
     * It has no pattern in it: a caustic that has bounced off sand has been
     * through a diffuse surface and is a wash, not a caustic.
     *
     * 0.09 is what it was when it rode on `caustic * 0.30` at the shipped
     * caustic strength, so this changes nothing until you move it.
     */
    bounce: 0.09,
    /**
     * How brightly a mote burns when it is standing in a sun shaft.
     *
     * Motes have always been lit by `lit`, and `lit` is the SEARCHLIGHT. In a
     * sunbeam a mote got the same flat ambient as one in open water, so the
     * beams were volume and never grit. This is the other half: the same
     * caustic field the shafts are made of, asked at the mote.
     */
    moteSun: 1.216,
    /**
     * How much of the sky an overhang takes away from the ambient dome.
     * 0 is the old behaviour — a ledge inside a cave lit as brightly as open
     * seabed. 1 puts it in full shadow.
     */
    skyAbove: 0.635,
    /**
     * How strongly the reference-art detail layers modulate rock and terrain.
     *
     * 1 is what shipped. The layers are renders of a rock spire and a cluster
     * of coral polyps, so their content is OBJECTS — at full strength they
     * tile as hundreds of dark circles rather than as surface grain. This
     * keeps the relief and stops the polyps being recognisable.
     *
     * AND IT MAKES ROCK BRIGHTER, NOT DARKER, WHICH IS WHY IT IS NOT THE
     * REPORT ABOUT THE FRONT ROCK. Measured off the art rather than argued
     * about: `rock-detail` has a mean red of 0.114 and a mean alpha of 0.542,
     * so `1 + (r - 0.5) * 0.62 + (a - 0.5) * 0.40` averages 0.784 at art 1 —
     * the detail layer costs a rock a fifth of its albedo. Around 0.4 it
     * averages 0.91, and at 0 exactly 1. Every step DOWN this slider is a step
     * brighter, so no setting of it can be what darkened anything.
     */
    artDetail: 0.4335,
    /**
     * How hard a transmissive surface bends what is behind it, in screen
     * fractions at full grazing. Glass, the jelly bells, the gate veils.
     *
     * 0 does not merely stop the bending: it collapses the scene back into a
     * single render pass, because refraction is the only reason to split it.
     * See the note at `scene:opaque`.
     */
    // Opt-in diagnostic now. The thin-shell difference was subtle on a bell,
    // but splitting the scene around it forces full HDR/MRF/depth tile stores
    // on Apple GPUs. The surface has its own refraction path and is unaffected.
    refraction: 0.35,
    /**
     * HOW FAST THE LIGHT DIES AS YOU GO DOWN. 1 is the authored progression.
     *
     * The depth bands in `config.ts` are a table — extinction, fog, ambient
     * and its level, interpolated by how deep the camera is — so the honest
     * lever is not a gain on the answer but a warp on the QUESTION. This
     * scales the depth the table is read at, and the sun's own per-metre
     * exponent with it, so 2 means 127 m is lit like 254 and every quantity
     * that depends on depth moves together instead of drifting apart.
     *
     * It does NOT move the zone name. `updateBands` only writes the lighting;
     * which biome you are in stays a fact about where you are.
     */
    depthFall: 0.93,
    /**
     * THE SAME QUESTION SIDEWAYS: how much water there is between the lens and
     * a surface, which is what separates the three lanes.
     *
     * Scales `fogDist`, and only that — the distance the transmittance is
     * raised over. The camera stands 68 m off the play plane for framing
     * reasons and 80% of that is already forgiven; this scales what is left.
     * Below 1 the far lanes come forward and hold their colour, above 1 they
     * recede into the haze.
     */
    zFall: 0.03,
    /**
     * WATER DENSITY. A flat multiplier on all three extinction channels.
     *
     * Distinct from `zFall` on purpose, even though both end up inside the
     * same exponent for surfaces: extinction also gates the searchlight beam,
     * every point light's travel term and the volumetric march, so this is
     * "thicker water" while `zFall` is "the lanes are further apart". Turning
     * this up localises light; turning `zFall` up only pushes the background
     * away.
     */
    density: 0.171,
    /**
     * HOW HARD THE PARALLAX LANES ARE PUSHED TOWARD THEIR AUTHORED TINT.
     *
     * The other half of why lane 3 is dark, and the half no fog slider can
     * reach. `LAYERS[2].tint` is [0.20, 0.28, 0.40] — a flat multiply on the
     * lane's albedo, applied on top of everything the water already took. At
     * 127 m the front of that lane is at roughly 0.36 blue transmittance
     * BEFORE the tint, and 0.14 after it. 0 keeps the lanes their own colour
     * and lets the water alone say how far away they are.
     */
    laneTint: 0.2,
    /**
     * HOW MUCH OF THE WATER'S OWN AMBIENT A SPECULAR LOBE GETS BACK.
     *
     * 1 is the physically correct amount for a uniform environment and is the
     * default, because this is a MISSING TERM rather than an effect. 0 is the
     * old behaviour, where a conductor was lit by the searchlight and
     * essentially nothing else; above 1 is a cheat for making metal read in
     * water too dark to justify it.
     */
    metalAmbient: 1,
    /**
     * THE ORE'S ACHROMATIC SELF-COLOUR, and the one deliberate cheat in the
     * mineral shading.
     *
     * Measured rather than felt: at 40 m the dome is [0.21, 0.52, 0.67] and
     * copper's F0 is [0.74, 0.31, 0.11], so their product normalises to
     * [0.97, 1.00, 0.48] — olive grey. Blue water has no red in it for copper
     * to be copper with, so no amount of `metalAmbient` will ever do this job.
     * Taking the ambient's LEVEL without its CAST gives [1.00, 0.39, 0.11].
     *
     * Metallic ore only. The six glowing minerals already carry an emission
     * colour; iron, copper and titanium are exactly the three that do not.
     */
    oreSheen: 0.55,
    /**
     * A multiplier on the emission the ARTIST authored, not a new light.
     *
     * Six of the eight minerals ship an emission colour and strength in the
     * manifest — silica 0.3, bio 0.85, abyssal 1.1, rare 1.2 — and this scales
     * all of them together. The other two, iron and copper, ship none and are
     * not given any: see `oreSheen` for why a conductor must not be emissive.
     */
    /**
     * Water haze — how much inscatter the water sends to the lens.
     *
     * 1 is exactly the band table as authored, so the default changes nothing.
     * Below it the water clears and distant terrain keeps its own contrast;
     * above it the frame fills in.
     *
     * This is the dial for "the picture looks washed out", and it is a
     * different thing from exposure. Exposure moves everything together;
     * inscatter is a floor added to everything, so it compresses the range
     * rather than shifting it. Measured, it is 57% of a pixel at 30 m and 76%
     * at 60 m, at every depth alike.
     */
    haze: 1,
    oreGlow: 1,
    /**
     * How much of the dome BEHIND a stone comes through it. See `crystalCoat`:
     * weighted to the silhouette, where a convex crystal is thinnest, which is
     * the opposite of where its reflection is brightest. Gems only — a
     * conductor transmits nothing.
     */
    oreTranslucency: 0.9,
    /**
     * How much diffuse a gem keeps when `gem glass` is on.
     *
     * 0.04 is very nearly none, which is the point: what makes a crystal read
     * as transparent is a dark interior against a bright edge, and a diffuse
     * term is a broad flat wash that fills the interior in. Not exactly zero —
     * a crystal in a cave with the lamp off should still be a shape rather
     * than a hole in the frame.
     */
    gemDiffuse: 0.04,
    /** How hard the detail set's height and cavity drive roughness. */
    surfaceWear: 1.0,
    /** Coverage a pane keeps face-on: body tint, grime, the second surface. */
    glassBody: 0.16,
    /** How hard a pane bends what is behind it, over the base refraction. */
    glassRefract: 1.0,
    /** Geometric specular antialiasing strength. */
    specAA: 1.0,
    /** How far the boat's viewports go toward glass. */
    subGlass: 1.0,
    /** Scale on the boat's cylindrical unwrap — its plate size. */
    subPanel: 1.0,
    /** Detail tiling on paint and metal. 4x finer than the seabed's 0.42. */
    builtDetail: 1.7,
    /** How hard the detail set modulates paint and metal. Rock keeps 1.0. */
    builtGrain: 0.0,
    /** Blue ambient a cabin keeps. Low is dry air, 1 is flooded. */
    cabinAmbient: 0.06,
    /**
     * Lamp intensity scale, applied on top of `LIGHT.searchIntensity` (12).
     * 0.14 brings a hull at 3 m from ~12.8 down to ~1.8, just under the
     * tonemap's shoulder. Only read when `physicalLamp` is on.
     */
    lampScale: 1.0,
    /** Lamp falloff, linear term. Shipped value is 0.055. */
    lampLinear: 0.055,
    /** Lamp falloff, quadratic. Shipped is 0.0030; inverse square would be 1. */
    lampQuadratic: 0.0030,
    /** Colour bleed strength. Higher than 1 because bloom is threshold-cut. */
    ssgi: 1.4,
    /**
     * Intensity of the point light each glowing ore node puts in the water.
     *
     * A crystal that is bright and lights nothing around it reads as a decal
     * stuck on a rock; the giveaway is that the stone it sits on stays grey.
     * One light per node fixes that for less than it sounds — the six emissive
     * minerals only, nothing on screen but a handful at once, against a budget
     * of 48 on the lowest tier and 160 on the highest.
     */
    oreLight: 1.1,
    /**
     * How much bigger a cluster is than the placement made it. Live, because
     * the seams are generated and cached and re-rolling them to try a size is
     * a reload — this is a multiply at draw time, so a drag is the next frame.
     *
     * SEPARATE FOR THE TWO HALVES OF THE SET. Silica, lithium, rare-earth, bio
     * and the abyssal crystal are spires and geodes that want to be landmarks;
     * iron, copper and titanium are crusts and nuggets that want to look
     * prised out of the rock. Split on `metallic > 0.5`, which is the same
     * line the shader already draws between a gem and a conductor.
     */
    crystalScale: 1,
    metalOreScale: 1,
    /**
     * Studio rig strength for a mineral on the refit turntable. 0 is off.
     *
     * THE ENGINE ALREADY HAD AN HDR IBL AND THE ORE COULD NOT REACH IT. The
     * display hull is lit by `studioTexS` — a real prefiltered environment,
     * sampled about the reflected direction with roughness picking the mip —
     * and the switch that turns it on is `params2.w`. Ore could not use that
     * field: positive values also declare baked per-vertex occlusion in uv.y
     * and packed albedo in uv.x, and a mineral mesh carries neither, so the
     * one attempt at it rendered the icons fully occluded and black.
     *
     * The NEGATIVE branch of that same field is the answer, and it was already
     * there for the enemy hulls: magnitude for the rig, sign for "this mesh has
     * no bake". Every clamp in `vsMesh` takes a negative to zero, so the AO,
     * the rivets and the packed colour all switch off without a comparison.
     *
     * THE NUMBER IS `hero`, so the thresholds in the shader are the thresholds
     * here: above 1.02 the stone also gets the studio ROOM to reflect, which is
     * the part that turns a flat shape into a photographed object. Below it,
     * only the three-point rig. 1.15 is a little over the boat's own 1.1,
     * because a crystal is smaller than a submarine and has further to travel
     * to fill the same panel.
     */
    oreStudio: 1.15,
    /**
     * The same dial for the DISPLAY HULL, which has always been on this path —
     * it is where the ore's was taken from — but as a constant with no way to
     * see what it was worth.
     *
     * 1.1 is exactly the 2.1 that was hard-coded at the call site, minus the 1
     * the shader adds to mean "display model". So the default changes nothing
     * and the boat and the cargo standing on the same turntable can now be
     * balanced against each other rather than one of them being a number in
     * the middle of a draw call.
     */
    subStudio: 1.1,
    /**
     * How much of a display object's own emission the refit panel keeps.
     *
     * 1 is whole. It was effectively 0.30 — the stand damp multiplies the
     * finished colour, and `shade` has already added the emission into it by
     * then, so a glowing mineral and the boat's running lights were being
     * discounted as though they were reflections of a sea that is not there.
     *
     * The hull's emission is scaled a second time by `sub2Emis` before it is
     * ever queued, so the running lights were landing at about a twelfth of
     * what they are in the water.
     */
    standEmissive: 1,
    /**
     * How much of the baked environment probe a reflection that missed the
     * screen-space march gets back. See ENV_GAIN_P in post.wgsl: the lookup it
     * scales was reading the mirrored direction until the screen-space normal
     * was corrected, so 0.16 was picked against the dark half of a probe whose
     * two halves differ by 89x.
     */
    envGain: 0.34,
    /**
     * How much of the hero's three-point studio rig to keep. 0 lights the boat
     * by the water alone, exactly like every other mesh; 1 is the rig at the
     * strength it has always had.
     */
    studioRig: 1.018,
    /**
     * How much of the baked occlusion the LAMP and the point lights believe.
     *
     * It was zero — `ao` reached the ambient and nothing else, so a searchlight
     * and a row of LEDs lit every crease on the hull as though it were a flat
     * plate. Not 1 either: baked AO is a sky-visibility measure and a lamp is a
     * point, so a hemisphere's answer over-darkens a point source.
     */
    lampAo: 1,
    /**
     * How much a point light wraps onto faces turned away from it.
     *
     * Was 0.3, unmeasured. A face pointing straight away from a light kept
     * thirty percent of it — soft fill in open water, and on a hull carrying
     * its own LEDs a flat wash across every face at once, which is the missing
     * deck-to-keel gradient.
     *
     * 0.029 by eye, which is very nearly none: the LEDs stop at the
     * terminator and the hull's shape comes from the key and its shadow
     * instead. The slider still reaches 0.3 if the wash is ever wanted back.
     */
    pointWrap: 0.029,
    /**
     * How much of the studio key light the height map is allowed to take away.
     *
     * The rig had no occlusion of any kind on its key: a flat directional add
     * from (-0.30, 0.88, 0.37) that no conning tower, no rock and no part of
     * the hull itself could block. The map now answers for it. 0 is the old
     * unshadowed key, 1 is the blocker taken at full strength.
     */
    rigShadow: 1,
    /**
     * Bioluminescence gain, for anything that makes its own light. It was being
     * scaled by the ambient level and then by its own coverage, so the deeper a
     * jellyfish went the dimmer its lantern got — see fsJelly.
     */
    bioGlow: 0.75,
    /**
     * How much of a glowing animal's light the water around it scatters back.
     * Surfaces already caught it through `points`; this is the medium, which is
     * the half that makes a glow read as a glow rather than as a bright sprite.
     */
    bioScatter: 0.5,
    /** Finest detail the surface normal resolves, in metres. Small is crisp. */
    normalEps: 0.34,
    /** Past this the normal folds back to the swell's, killing distant sparkle. */
    normalFlat: 42,
    /** Octaves of detail in the shading normal. The definition dial. */
    detailOct: 1,
    /**
     * How much light comes THROUGH a blade of weed. The inverted normal used to
     * fake this by accident, which is what made the kelp glow; correcting the
     * normal took it away, so here it is on purpose and on a slider.
     */
    leaf: 1.6,
    /** Depth over which the water runs from its shallow tint to its deep one. */
    tintDepth: 16,
    /**
     * Relaxation steps solving where the ray meets the DISPLACED surface. Zero
     * is the old flat plane, where wave height changed the shading and nothing
     * else. One correction is enough for the deliberately bounded wind sea.
     */
    waveRelax: 1,
  };
  private postData: Float32Array<ArrayBuffer> = new Float32Array(168);
  private coneBuf!: GPUBuffer;
  /** The baked environment: one mipped equirect, and the sampler for it. */
  private envTex!: GPUTexture;
  private studioTex!: GPUTexture;
  private envSampler!: GPUSampler;
  private lightsData: Float32Array<ArrayBuffer> = new Float32Array(MAX_LIGHTS * 8);
  private lightCountData: Float32Array<ArrayBuffer> = new Float32Array(4);
  private lightCount = 0;
  /**
   * The volumetric pass consumes only lights whose range is tagged negative.
   * Keep that already-defined subset contiguous so every half-resolution pixel
   * does not scan the full surface-light list just to reject most of it.
   */
  private scatterLightsData: Float32Array<ArrayBuffer> = new Float32Array(MAX_LIGHTS * 8);
  private scatterLightCountData: Float32Array<ArrayBuffer> = new Float32Array(4);
  private scatterLightCount = 0;
  /** Lights honoured this frame; from the tier, never above MAX_LIGHTS. */
  private lightCap = 48;
  /**
   * Slots that have reported an overflow at least once, so the warning is
   * printed per slot rather than per frame.
   *
   * OVERFLOW USED TO BE SILENT BY DESIGN, and the F3 readout was the only tell.
   * That is a reasonable choice for a counter and a bad one for a defect: a
   * dropped instance does not look like a dropped instance, it looks like the
   * scenery going dark, and it took a very long time to find on that basis.
   * The counter stays; it now says so out loud, once, with the number needed.
   */
  private warned = new Set<string>();

  private globalsLayout: GPUBindGroupLayout;
  private globalsBind: GPUBindGroup;

  private terrainPipe: GPURenderPipeline;
  /**
   * How long the GPU actually spent on the last frame it reported, in ms.
   *
   * The F3 readout could only ever say "CPU total, and everything else" —
   * and "everything else" is two completely different situations wearing the
   * same number: a GPU that cannot keep up, and a main thread idling against
   * vsync with the frame already finished. They want opposite fixes, and the
   * subtraction cannot tell them apart. This can.
   *
   * Zero when the adapter has no `timestamp-query`, which the readout reports
   * as unavailable rather than as zero.
   */
  gpuMs = 0;
  /**
   * Measured GPU milliseconds per named pass, smoothed. Empty when per-pass
   * timing is off or unavailable.
   *
   * A FRAME TOTAL SAYS THE GPU IS THE PROBLEM AND STOPS THERE. That was enough
   * while the question was "is this CPU or GPU bound"; it is not enough for the
   * one after it, which is always "so which pass". Answering that by commenting
   * passes out changes the picture as well as the cost, and the two most
   * expensive things in this frame — the volumetric march and the bloom chain —
   * are exactly the ones whose absence is obvious enough to mislead you about
   * how much they were worth.
   */
  readonly gpuPass: Record<string, number> = {};
  /**
   * Per-pass timing, off by default.
   *
   * A timestamp write is a real synchronisation point on some drivers, and the
   * frame it is measuring is the frame it perturbs — a tiled mobile GPU in
   * particular can lose a chunk of its pass merging to them. Two queries a
   * frame is noise; forty is a measurement that changes what it measures. So
   * this is a thing that ought to be switched off once the question is
   * answered — particularly before shipping to a phone, where the pass merging
   * a tiled GPU does is exactly what a stage-boundary timestamp prevents.
   *
   * ON BY DEFAULT WHILE THE PHONE WORK IS LIVE, though, because it is the only
   * source of a GPU number at all: there is no separate whole-frame bracket, so
   * without this the readout has nothing to say. Measured at about 1.4 us a
   * mark against a 16.6 ms frame.
   */
private _gpuPassTiming = true;
  get gpuPassTiming() { return this._gpuPassTiming; }
  set gpuPassTiming(v: boolean) {
    if (v === this._gpuPassTiming) return;
    this._gpuPassTiming = v;
    // WIPED ON EVERY TRANSITION, not only on switch-off. Numbers that stop
    // updating do not stop being drawn, so a stale breakdown from before a
    // quality change or a resize reads exactly like a live one — and it would
    // be describing a frame that is no longer being rendered.
    for (const k of Object.keys(this.gpuPass)) delete this.gpuPass[k];
  }
  private tsSet?: GPUQuerySet;
  /** Queries used so far this frame. 0 and 1 are the whole-frame bracket. */
  private tsUsed = 2;
  /** Label of each PAIR allocated this frame, in allocation order. */
  private tsNames: string[] = [];
  /** The labels that were in flight when each readback slot was filled. */
  private tsFlight: (string[] | null)[] = [];
  /** Labels already reported as duplicated, so the warning is once each. */
  private tsDupWarned = new Set<string>();
  /**
   * A 1x1 render pass with a three-vertex draw: the mark every timestamp-only
   * bracket writes from.
   *
   * A PASS THAT ENCODES NO WORK GETS NO TIMESTAMPS, and gets them silently.
   * Measured on Metal via Dawn: an empty compute pass writes NEITHER end, with
   * no error and no warning — the query keeps whatever it held, which for a
   * freshly created set is zero. An empty render pass is worse, because it
   * writes the beginning and not the end, so a bracket built from one looks
   * like it half worked.
   *
   * That is why the whole-frame bracket has been reading zero for as long as
   * it has existed, and why the F3 line has been reporting no timestamp
   * support on adapters that plainly have it.
   *
   * A single draw is enough to make the pass real. Measured at about 1.4 us —
   * against a 16.6 ms frame, a handful are a rounding error, and they are the
   * difference between a number and no number at all.
   *
   * AND IT IS A RENDER PASS RATHER THAN A COMPUTE ONE, WHICH IS THE SECOND
   * FINDING AND THE MORE SURPRISING. Compute and render timestamps on this
   * driver are not ordered against each other: a compute mark dropped into a
   * run of render passes lands wherever it likes, so a timeline built from a
   * mixture of the two is not a timeline at all. The deltas either side of one
   * come back negative, get filtered as impossible, and the profile arrives
   * empty while the query set is being written perfectly well — which is
   * exactly what "timestamps enabled but reading zero" was.
   *
   * One clock, therefore. Every mark in the frame is a render pass, so every
   * delta is a real interval. Bracketing a COMPUTE dispatch with two of these
   * still tracks it: measured 0.101 ms at 64 workgroups against 0.310 ms at
   * 1024, so the wake and the particle sim are timed by the render clock and
   * report honest numbers.
   */
  private tsNop?: GPURenderPipeline;
  /** The 1x1 attachment those marks draw into, made once. */
  private tsNopView?: GPUTextureView;
  /**
   * Readbacks that have completed with a usable frame time.
   *
   * Here so the readout can tell "this adapter has no timestamps" apart from
   * "it has them and they are all coming back zero", which are two different
   * problems that looked identical for as long as the bracket was broken —
   * the F3 line said the adapter lacked a feature it plainly advertised.
   */
  tsSamples = 0;
  private tsDud = 0;
  /** True when the device advertised timestamps, whatever they then did. */
  get tsSupported() { return this.tsSet !== undefined; }
  private tsResolve?: GPUBuffer;
  /**
   * Two staging buffers, cycled.
   *
   * `mapAsync` resolves a frame or more later, and a buffer cannot be written
   * while it is mapped — so with one buffer the readback either stalls the
   * frame or silently drops most of them. The reading is a frame or two stale,
   * which for a number a human is watching is not a cost at all.
   */
  private tsRead: GPUBuffer[] = [];
  private tsBusy = [false, false];
  private tsFlip = 0;

  private terrainBinds: GPUBindGroup[] = [];
  /** The uniform behind each of those binds, so focus can follow the camera. */
  private layerBufs: GPUBuffer[] = [];
  /** Scratch for the per-frame layer uniform write. */
  private layerScratch = new Float32Array(8);

  private meshPipe: GPURenderPipeline;
  private jellyPipe!: GPURenderPipeline;
  private glassPipe!: GPURenderPipeline;
  /**
   * World-anchored text, and the plates and leaders that go with it.
   *
   * Public because the caller writes into it during its own draw pass — the
   * renderer has no idea what wants labelling — and it is flushed at present
   * time into the swapchain, under the HUD canvas and over everything else.
   */
  readonly text: TextBatch;
  /**
   * The render target in CSS pixels, which is what a caller placing a label
   * needs and is not the same as the device size — the pixel budget can scale
   * the backing store down without the layout moving.
   */
  get cssWidth(): number { return this.gpu.width / this.gpu.dpr; }
  get cssHeight(): number { return this.gpu.height / this.gpu.dpr; }
  /** Device pixels per CSS pixel, AFTER the pixel budget has had its say. */
  get gpuDpr(): number { return this.gpu.dpr; }
  private particlePipe: GPURenderPipeline;
  /**
   * The same pipeline with the motion vector and the G-buffer back ON, which is
   * what shipped until today. Kept only so the change can be A/B'd live: a
   * write mask is baked into a pipeline at creation, so a checkbox for it has
   * to be a second object rather than a uniform. One pipeline is cheap; being
   * unable to bisect a rendering change is not.
   */
  private particlePipeAux: GPURenderPipeline;
  private particleBind!: GPUBindGroup;

  private brightPipe: GPURenderPipeline;
  private blurPipe: GPURenderPipeline;
  private compositePipe: GPURenderPipeline;
  private postSampler: GPUSampler;
  private brightBind!: GPUBindGroup;
  private blurBinds: GPUBindGroup[] = [];
  private blurUniformBinds: GPUBindGroup[] = [];
  private compositeBind!: GPUBindGroup;

  private slots = new Map<string, MeshSlot>();

  /**
   * Per-frame draw accounting for the F3 readout.
   *
   * The desktop is vsync-capped, so frame rate tells you nothing on its own and
   * every previous slow-zone report here had to be chased by hand-wrapping
   * `drawIndexed` from the devtools console. This makes the same numbers
   * readable on the device that is actually struggling, which is the only place
   * they matter. Cleared in `resetQueue`, so it costs a few adds per draw call.
   *
   * `slotOverflow` and `lightOverflow` are the two silent failures in this file:
   * `queue` and `addLight` both return past capacity with no warning, and draw
   * order decides who loses.
   */
  /**
   * ELIMINATION SWITCHES, for finding where a frame's time actually goes.
   *
   * Survival measured at 16.4 ms of GPU with nothing alive on screen, which is
   * the whole 60 fps budget spent before any gameplay — and a frame that is
   * already full cannot be reasoned about, only bisected. Each flag removes one
   * cost and nothing else, so `gpuMs` from the telemetry names the culprit
   * instead of implicating everything at once.
   *
   * Dev-only and off the hot path: five boolean reads per frame. Set them from
   * the console via `ABYSS.renderer.probe`.
   */
  /** Live depth-of-field numbers, for the console. See `probe.cocView`. */
  readonly dofInfo = {
    stop: 0, maxRadiusPx: 0, scale: 0, focusDist: 0, viewZoom: 0,
    perLane: [0, 0, 0] as number[],
  };

  readonly probe = {
    /**
     * Paint the depth-of-field circle of confusion instead of the scene.
     *
     * Black is "in focus", white is "blur this as hard as the aperture allows".
     * It separates the two halves of an effect that otherwise fails silently:
     * if the frame is black at f/1.2 the CoC is wrong, and if it is white while
     * the picture stays sharp the gather is.
     */
    cocView: false,
    /**
     * The wake. `false` skips the whole simulation, and the composite then
     * reads a field that is simply not being stirred.
     *
     * Priceable on purpose: the solve is 23 render passes a frame — one advect,
     * one divergence, sixteen pressure sweeps and a gradient — and while each
     * is a few tens of thousands of texels, twenty-three of anything is worth
     * being able to switch off and measure against the picture it buys.
     */
    /**
     * Off on the mobile tier until it has been measured there.
     *
     * The wake is the newest and by far the heaviest thing in the frame — 23
     * render passes and a 1/5-resolution fluid — and a phone reported the
     * picture breaking down under nitro, which is exactly when the field is
     * being driven hardest. That is a correlation, not a diagnosis, and I have
     * not reproduced it. Turning the new thing off on the tier that reported
     * the problem is the honest move: it either fixes it, which tells us where
     * to look, or it does not, which tells us more.
     */
    wake: true,
    particles: true,
    lights: true,
    /** Instanced meshes: scenery, actors, the boat. */
    instances: true,
    /**
     * The per-frame instance UPLOAD, separately from the draws.
     *
     * Every live instance is re-sent every frame — model, prevModel, tint,
     * emissive, params, params2, pbr: 52 floats, 208 bytes. At the hundred
     * thousand instances a populated zone carries that is 20.8 MB a frame, and
     * `writeBuffer` is a staged copy, so it is paid twice: once by the CPU into
     * the staging ring and once by the GPU copying it out. At 120 fps that is
     * about 2.5 GB a second of pure copy.
     *
     * It is invariant to window size, to resolution, and to every effect switch
     * on the ladder — which is the shape of the thing that has survived all of
     * them. Gated separately from `instances` so the DRAWS and the UPLOAD can
     * be measured apart: turn off the draws and the difference is rasterising,
     * turn off the upload as well and the difference is the bus.
     */
    instanceUpload: true,
    /** The two parallax background layers. The near layer always draws. */
    /**
     * How many terrain layers to draw, play layer first. 3 is all of them, 1 is
     * the play layer alone.
     *
     * A count rather than a switch because the two parallax layers do not cost
     * the same and are not worth the same: measured uncapped at 750 m, dropping
     * both takes 1.1 ms off a 9.4 ms frame, and the far one is the dimmer and
     * more occluded half of that.
     */
    farLayers: 3,
    /** The seabed and its parallax slabs. The other half of the scene pass. */
    terrain: true,
    /** The caustic splat pass. The map is wiped when this is off, not frozen. */
    caustics: true,
    /**
     * The top-down height map: a depth-only view of the world from above, so
     * anything can ask what is overhead without marching a screen-space ray
     * through a buffer that only holds the nearest surface.
     */
    heightMap: true,
    /**
     * Henyey-Greenstein on the lamp's volume: forward scattering, so the beam
     * depends on the angle it is seen from instead of returning the same light
     * in every direction.
     */
    phase: true,
    /**
     * The scene pass shading itself against the height map. Sun only — the
     * ambient is a dome and `ao` is what occludes that.
     */
    sunShadow: true,
    /** The narrow bright core inside each sun shaft. */
    shaftCore: true,
    /** Longitudinal structure along a shaft, from a cross-sun shear. */
    shaftGrain: true,
    /** The caustic window leading up-sun, so deep shafts stay on the map. */
    shaftLead: true,
    /** The studio softbox behind screen-space reflections. */
    studio: true,
    /** Motes lighting up inside sun shafts. */
    moteSun: true,
    /** The ambient dome occluded by whatever is overhead. */
    skyAbove: true,
    /** The reference-art detail layers on the terrain. */
    artDetail: true,
    /**
     * Screen-space refraction through translucent meshes.
     *
     * ON, AND THE REASON IT WAS OFF NO LONGER HOLDS. The note here read "its
     * render-pass split costs far more than the small bend adds to jelly
     * tissue", and that was a fair trade when a jellyfish membrane was the only
     * thing in the game that refracted: the split buys a backdrop copy, and a
     * barely-visible wobble on a bell is not worth a pass.
     *
     * The habitat glazing changed the arithmetic. A window is the one surface
     * where refraction is not decoration — it is what says there is a pane in
     * the opening rather than a hole, and without it a lit cabin reads as an
     * open box. The split now pays for something structural.
     *
     * Still a checkbox, and the cost is still real: it splits `scene:opaque`
     * from `scene:translucent` and copies the backdrop rectangles between them.
     * See `backdropRegions` for how little of the frame that actually is.
     */
    refraction: true,
    /**
     * Extend that refraction to jellyfish and foam as well as glass. Off: see
     * `backdropRegions` for why the animals are what make it expensive.
     */
    jellyRefraction: false,
    /**
     * A physical falloff and a matched intensity for the searchlight, instead
     * of the flat near field that saturates anything pale within twenty metres.
     * Off is exactly what shipped.
     */
    physicalLamp: false,
    /** Conservative regional backdrop copies; off restores the full-frame A/B. */
    backdropBounds: true,
    /** Use the baked underwater probe on the sea instead of the live sky. */
    surfaceSky: false,
    /**
     * Bend the submerged scene through the screen-space surface normal.
     * Disabled by default: the reference look gets its motion from shape,
     * Fresnel and glint, while this extra depth-layer sample can duplicate hard
     * silhouettes and costs bandwidth across the visible sea.
     */
    surfaceRefraction: false,
    /**
     * The older centimetre-scale reflection wrinkles and reflected cloud
     * noise. Kept as an explicit, slower look switch: it has character, but at
     * grazing angles it can turn into the thin oily streaks we removed.
     */
    surfaceMicro: false,
    /**
     * Reject differently-deep neighbours from TAA's history clamp. This is a
     * useful silhouette diagnostic, but the composite sea does not write its
     * own depth, so hidden seabed edges can otherwise become dark contours.
     */
    taaDepthClamp: false,
    /**
     * The depth-of-field bleed guard: a tap may only light this pixel if its
     * own circle of confusion reaches it. See `fsDof`.
     */
    dofGuard: true,
    /**
     * PUT THE MOTES BACK IN THE G-BUFFER. Off is correct and is the default;
     * this exists so today's change can be A/B'd against the frame it changed.
     *
     * A speck of silt stamping its own motion vector and its own material over
     * the opaque surface behind it was a bug in both channels, but it was a
     * bug with a LOOK — every mote wrote roughness 1 and f0 0 across the rock
     * it drifted past — and "the front rock lost a light leak" is exactly the
     * shape of report that a removed artefact makes. One click settles it.
     */
    moteGbuf: false,
    /** The ambient's specular half. See `ambientSpec` in scene.wgsl. */
    metalAmbient: true,
    /** The ore's achromatic self-colour. See `crystalCoat`. */
    oreSheen: true,
    /**
     * Derive text coverage from the SIGNED DISTANCE FIELD rather than from the
     * bitmap atlas's own coverage. Both are built and both stay resident, so
     * this is a branch in the fragment and the two can be compared frame to
     * frame. See `fsText`.
     */
    textSdf: true,
    /** The whole crystal coat: facets, dispersion, interior, glow. */
    crystal: true,
    /**
     * Gems shaded as glass — almost no diffuse, so the body stays dark and the
     * edges carry it. Off shades them like any other solid.
     */
    gemGlass: true,
    /**
     * Glass drawn in the translucent pass, so a window transmits what is behind
     * it. Off returns the pane to a fully covered surface — the old opaque
     * look — without changing which pass it draws in, which is what makes it a
     * fair comparison. See `fsGlass`.
     */
    glassTransmit: true,
    /**
     * Detail maps projected on three axes with a per-axis tangent frame, rather
     * than one planar projection on world XY with the tangent normal added
     * straight to the world normal. See `detailTri`.
     */
    triplanar: true,
    /** Roughness driven by the detail set's height and cavity. */
    surfaceWear: true,
    /**
     * Split-sum DFG plus multi-scatter energy compensation, instead of the
     * roughness-aware Schlick approximation. See `dfgAB` in scene.wgsl.
     */
    dfg: true,
    /** Widen roughness where the normal swings within one pixel. */
    specAA: true,
    /** Surface grain on built materials at all. Off leaves steel spotless. */
    builtGrain: true,
    /** One-bounce colour bleed on the AO taps. */
    ssgi: true,
    /** Habitat interiors lit as air rather than as water. */
    cabinAir: true,
    /** The boat's `MAT.glow` viewports shaded as glass instead of lit plate. */
    subGlass: true,
    /** The boat's paint on the generated panel-seam layer. */
    subPlate: true,
    /** Atmospheric reddening of the sun near the horizon. */
    sunset: true,
    /** Water inscatter. Off is perfectly clear water — a diagnostic, not a look. */
    haze: true,
    /**
     * Whether a display object on the refit panel keeps its own light. Off
     * restores the flat 0.30 the stand damp used to apply to everything,
     * emission included.
     */
    standEmissive: true,
    /**
     * A REQUEST, NOT A SETTING. Set by the tuning panel and cleared by the
     * world the moment it acts on it — the panel only knows how to draw
     * toggles, and this is a button.
     */
    resetOre: false,
    /** A point light in every glowing ore node, so it lights its own rock. */
    oreLight: true,
    /**
     * Each particle KIND, on its own switch. Matches `KIND` in particles.ts.
     *
     * A diagnostic first and a profiler second: culling happens in the vertex
     * stage, so unticking one is both "is this the thing I am looking at" and
     * "what does this kind cost me".
     */
    pKind: {
      /** SILT — the dim haze. 87 percent of the 24k ambient field. */
      ambient: true,
      /** PLANKTON — the bright specks. The other 13 percent. */
      plankton: true,
      /** Wake, torpedo trails, breach froth. The most numerous emitted kind. */
      bubble: true,
      /** Sparks, embers, bioluminescent flecks. */
      spark: true,
      /** Explosion wreckage. Opaque. */
      debris: true,
      /** Sediment plumes, ink, the bio haze. The big ones. */
      cloud: true,
      /** Ballistic droplets from water entry and exit. */
      spray: true,
    },
    /**
     * The studio key light shadowing itself against the height map. Separate
     * from `sunShadow` because it is a different light, a different stride and
     * a different falloff — and because it only ever touches the hero, so
     * switching it off is a question about the boat, not about the sea.
     */
    rigShadow: true,
    /**
     * The HUD overlay, which is not the renderer's to draw and is measured
     * here anyway.
     *
     * It is a second canvas the browser composites over this one, so no pass in
     * this file can see it and no switch in this file could price it — and it
     * was, until its budget was capped, the largest surface in the frame. The
     * flag lives in the probe block because that is where the instruments are;
     * `main` reads it.
     */
    hud: true,
    /** Bloom's bright pass and blur chain. */
    bloom: true,
    /** The water surface plane. */
    surface: true,
    /**
     * Fraction of the tier's volumetric march to run. 1 is full, 0 skips it.
     * The inscatter is renormalised by this, so a reduced march is dimmer only
     * by its own sampling error rather than by a factor of the step count.
     */
    volumetric: VOLUMETRIC_SCALE,
  };

  readonly stats = {
    terrainTris: 0,
    terrainDraws: 0,
    instTris: 0,
    instDraws: 0,
    particles: 0,
    slotOverflow: 0,
    lightOverflow: 0,
    lights: 0,
    /** Whether refraction requested a current-frame backdrop this frame. */
    backdropEnabled: false,
    /** Copied/resolved pixels and disjoint regions after conservative merging. */
    backdropPixels: 0,
    backdropRegions: 0,
    backdropCoverage: 0,
    /** What `lights` is measured against, so the HUD cannot report a stale cap. */
    lightCap: 48,
    /** Fullest instance slots: [name, triangles, count, used 0..1, capacity]. */
    top: [] as [string, number, number, number, number][],
  };
  /**
   * The last COMPLETED frame's counters, which is what anything outside the
   * renderer should read.
   *
   * `stats` is a working set: `resetQueue` zeroes it at the top of the world's
   * draw and it is filled across two phases — instance counts while the world
   * queues, terrain and particles while the frame encodes. Anything that reads
   * it in between gets a half-built answer, and the half-built answer looks
   * exactly like a real one: TRIS 0, DRAWS 0, parts 0, with the light count
   * still showing last frame's because that field happens not to be reset.
   *
   * That is precisely what happened when the performance readout moved onto the
   * GPU, because the only window in which text can be added to the batch is
   * between those two phases. The reader was not wrong to read there; the
   * counters were wrong to be readable there.
   *
   * So the numbers are published once, at the end of the frame that produced
   * them. A reader gets a whole frame or the previous whole frame, never a
   * frame in progress — and it no longer matters when it asks.
   */
  readonly published: Renderer['stats'] = {
    terrainTris: 0, terrainDraws: 0, instTris: 0, instDraws: 0, particles: 0,
    slotOverflow: 0, lightOverflow: 0, lights: 0,
    backdropEnabled: false, backdropPixels: 0, backdropRegions: 0, backdropCoverage: 0,
    lightCap: 48, top: [],
  };
  private meshLayout: GPUBindGroupLayout;

  private samples: number;

  /**
   * `envData` is the baked probe blob, fetched before the renderer is built —
   * see `main.ts`. Null when the fetch failed, which `buildEnvTexture` degrades
   * into flat per-depth colours from the inlined SH rather than a crash.
   */
  constructor(
    private gpu: Gpu, private quality: Quality, detail: DetailTextures,
    envData: ArrayBuffer | null = null,
  ) {
    const device = (this.device = gpu.device);
    const SAMPLES = (this.samples = quality.samples);
    this.fxaa = quality.fxaa;
    this.taa = quality.taa;
    this.lightCap = Math.min(MAX_LIGHTS, quality.maxLights);
    this.stats.lightCap = this.lightCap;

    // The composite reads the scene depth buffer directly. Its WGSL type has to
    // match the sample count, and the march length is a tier setting, so the
    // post source is specialised here rather than paid for as a uniform.
    let postSrc = postCode
      .replace(
        'let steps = i32(max(1.0, round(f32(VOL_STEPS) * U.probe.x)));',
        `let steps = i32(max(1.0, round(${quality.volumetricSteps}.0 * U.probe.x)));`,
      )
      .replace('VOL_GAIN', VOL_GAIN.toFixed(2))
      .replaceAll('DOF_TAPS', `${quality.dofTaps}`)
      .replaceAll('ENV_SH_TABLE', ENV_SH_ALL
        .flatMap((probe) => probe.map((c) => `vec3<f32>(${c.map((v) => v.toFixed(5)).join(', ')})`))
        .join(', '))
      .replaceAll('ENV_SH_COUNT', `${ENV_SH_ALL.length * 9}`)
      .replaceAll('ENV_LAYER_COUNT', `${ENV_LAYERS}`)
      .replaceAll('ENV_BAND_TABLE', ENV_BANDS.map((v) => v.toFixed(1)).join(', '))
      .replaceAll('ENV_SEL_Y', 'G.camPos.y')
      .replaceAll('STUDIO_MIP_MAX', STUDIO_MIP_MAX.toFixed(1))
      .replaceAll('ENV_MIP_MAX', (ENV_MIPS - 1).toFixed(1))
      // Full-resolution pixels at maximum defocus. Wide enough for the coins to
      // read as coins; much wider and the gather goes sparse at this tap count
      // and the disc breaks into its own samples.
      .replaceAll('DOF_RADIUS', '11.0');
    if (SAMPLES === 1) {
      postSrc = postSrc
        .replace('texture_depth_multisampled_2d', 'texture_depth_2d')
        .replace('textureLoad(depthTex, px, 0)', 'textureLoad(depthTex, px, 0)');
    }

    // An unused second output is not free: it is another full-resolution
    // attachment on the pass this renderer spends most of its frame in. So the
    // motion target exists in the source only when something reads it.
    // The G-buffer sits after the motion vector, so its location depends on
    // whether TAA is on — same conditional-slot trick the motion field already
    // uses, because a fragment shader writing a location with no attachment is
    // invalid rather than ignored.
    const mrfLoc = this.taa ? 2 : 1;
    const withMotion = (src: string) => src
      .replace('MOTION_FIELD', this.taa ? '  @location(1) mv : vec2<f32>,' : '')
      .replace('MOTION_ASSIGN', this.taa ? '  o.mv = mv;' : '')
      .replace('MRF_FIELD', `  @location(${mrfLoc}) mrf : vec4<f32>,`)
      .replace('MRF_ASSIGN', '  o.mrf = mrf;');
    // Motion vectors are a plain overwrite even where colour blends: a
    // translucent surface still wants the resolve to follow ITS movement, not
    // the water behind it, and averaging two objects' velocities would only
    // point at a third thing that is not there.
    /**
     * `aux: false` makes a pipeline write COLOUR ONLY, leaving the motion
     * vector and the G-buffer alone.
     *
     * Those two targets carry no blend state, so they REPLACE. A particle
     * therefore stamped its own motion and its own material over whatever
     * surface was behind it — and particles do not write depth, so "behind it"
     * is the opaque scene, at full strength, in a disc the size of the mote.
     *
     * The G-buffer was the visible half. Motes write `vec4(0, 1, 0, 0)`, so
     * f0 became 0, and `ssr()` opens with `if (m.b < 0.02) { return 0; }` —
     * every mote punched a hole in the reflection of the rock behind it. That
     * is the field of dark circles: not the mote's colour, which is additive
     * and cannot darken anything, but the REFLECTION IT DELETED. It explains
     * the whole shape of the report — only visible on bright reflective
     * surfaces, gone when `env reflection` went to zero, worse the moment the
     * studio gave those surfaces more to lose, and no transparency at all,
     * because a deleted reflection is not blended.
     *
     * The motion vector was the invisible half: TAA was reprojecting the scene
     * behind each mote from the MOTE'S velocity.
     *
     * A speck of drifting silt is not a surface and has no business in either
     * target. The shader has said so in a comment for a long time — "Motes are
     * not a surface: nothing reflects off a speck of drifting silt" — while
     * writing to it anyway.
     */
    const sceneTargets = (
      colour: Partial<GPUColorTargetState> = {}, aux = true,
      material: Partial<GPUColorTargetState> = {},
    ) => {
      const off = aux ? {} : { writeMask: 0 };
      return this.taa
        ? [{ format: HDR_FORMAT, ...colour },
           { format: MOTION_FORMAT as GPUTextureFormat, ...off },
           { format: MRF_FORMAT, ...off, ...material }]
        : [{ format: HDR_FORMAT, ...colour }, { format: MRF_FORMAT, ...off, ...material }];
    };
    // Sized off the swapchain at a fifth. Built BEFORE the particles, which
    // sample it.
    this.fluid = new Fluid(device, gpu.width, gpu.height);

    this.particles = new Particles(
      device, quality.ambientParticles, quality.dynamicParticles, withMotion,
      this.fluid.current,
    );
    // One source of truth for the falloff: the mesher merges runs by the
    // brightness this produces, so the two must not drift apart.
    const sceneSrc = withMotion(sceneCode)
      .replaceAll('AO_EXPONENT', AO_EXPONENT.toFixed(2))
      // The whole diffuse environment, as nine literals in the source. It never
      // changes at run time, so a uniform slot and a buffer write every frame
      // would be paying rent on a constant.
      .replaceAll('ENV_SH_TABLE', ENV_SH_ALL
        .flatMap((probe) => probe.map((c) => `vec3<f32>(${c.map((v) => v.toFixed(5)).join(', ')})`))
        .join(', '))
      .replaceAll('ENV_SH_COUNT', `${ENV_SH_ALL.length * 9}`)
      .replaceAll('ENV_LAYER_COUNT', `${ENV_LAYERS}`)
      .replaceAll('ENV_BAND_TABLE', ENV_BANDS.map((v) => v.toFixed(1)).join(', '))
      .replaceAll('ENV_SEL_Y', 'G.camPos.y')
      .replaceAll('STUDIO_MIP_MAX', STUDIO_MIP_MAX.toFixed(1))
      .replaceAll('ENV_MIP_MAX', (ENV_MIPS - 1).toFixed(1));
    const sceneModule = device.createShaderModule({ code: sceneSrc, label: 'scene' });
    const postModule = device.createShaderModule({ code: postSrc, label: 'post' });
    checkShader(sceneModule, 'scene', sceneSrc);
    // This one was not being checked, and a placeholder left unsubstituted in
    // it took the particle SIMULATION down with the renderer — a compute
    // pipeline in the same module — which invalidated the whole frame's command
    // buffer and drew nothing, with no error anywhere upstream of the device.
    checkShader(this.particles.renderModule, 'particles', this.particles.source);
    checkShader(postModule, 'post', postSrc);

    // --- shared globals bind group ---------------------------------------
    this.globalsBuf = makeUniform(device, this.globalsData.byteLength, 'globals');
    this.lightsBuf = device.createBuffer({
      size: this.lightsData.byteLength,
      usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_DST,
      label: 'lights',
    });
    this.lightCountBuf = makeUniform(device, 16, 'light-count');
    this.scatterLightsBuf = device.createBuffer({
      size: this.scatterLightsData.byteLength,
      usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_DST,
      label: 'scatter-lights',
    });
    this.scatterLightCountBuf = makeUniform(device, 16, 'scatter-light-count');
    // One float per beam ray: the normalised distance to the first occluder.
    this.coneBuf = device.createBuffer({
      size: Math.max(256, LIGHT.shadowBins * 4),
      usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_DST,
      label: 'cone-shadow',
    });

    // The environment, decoded and uploaded once. See `envMap.ts`.
    this.envTex = buildEnvTexture(device, envData);
    // The studio softbox, for reflections that leave the screen. See
    // `studioMap.ts` — it is generated, not shipped, and costs 40 KB of VRAM.
    this.studioTex = buildStudioTexture(device);
    this.envSampler = device.createSampler({
      // Repeat across the seam in longitude and clamp at the poles: an
      // equirect wraps left to right and does not wrap top to bottom, and a
      // repeating V would put the sky under the floor at grazing angles.
      addressModeU: 'repeat', addressModeV: 'clamp-to-edge',
      magFilter: 'linear', minFilter: 'linear', mipmapFilter: 'linear',
      label: 'env',
    });

    this.globalsLayout = device.createBindGroupLayout({
      label: 'globals',
      entries: [
        { binding: 0, visibility: GPUShaderStage.VERTEX | GPUShaderStage.FRAGMENT, buffer: { type: 'uniform' } },
        { binding: 1, visibility: GPUShaderStage.FRAGMENT, buffer: { type: 'read-only-storage' } },
        { binding: 2, visibility: GPUShaderStage.FRAGMENT, buffer: { type: 'uniform' } },
        { binding: 3, visibility: GPUShaderStage.FRAGMENT, sampler: { type: 'filtering' } },
        {
          binding: 4,
          visibility: GPUShaderStage.FRAGMENT,
          texture: { sampleType: 'float', viewDimension: '2d-array' },
        },
        { binding: 5, visibility: GPUShaderStage.FRAGMENT, buffer: { type: 'read-only-storage' } },
        { binding: 6, visibility: GPUShaderStage.FRAGMENT, sampler: { type: 'filtering' } },
        // 2d-array: the environment is now FIVE probes, one per depth band, and
        // the shader blends the two either side of the camera. The layout has to
        // say so — a view dimension that disagrees with its layout is a device
        // error at bind time, not a compile error, so nothing static catches it.
        { binding: 7, visibility: GPUShaderStage.FRAGMENT,
          texture: { sampleType: 'float', viewDimension: '2d-array' } },
        // The height map, so the SCENE pass can shade itself. Everything in
        // this group is shared by terrain, meshes, jellies and particles, which
        // is why it is declared here once rather than added per pipeline.
        { binding: 8, visibility: GPUShaderStage.FRAGMENT, texture: { sampleType: 'depth' } },
        // The caustic map, so a MOTE can ask whether it is standing in a sun
        // shaft. Nothing in the scene pass reads it — a group entry that no
        // pipeline in a pass declares is legal and free — but the particle
        // pass binds this same group, and this is the only way it gets there.
        { binding: 9, visibility: GPUShaderStage.FRAGMENT, texture: { sampleType: 'float' } },
        // The studio softbox, so the SCENE pass can reflect it with the real
        // shading normal. The composite already has it, but the composite only
        // knows the normal it can reconstruct from depth — see `studioS` in
        // scene.wgsl for why that is not good enough for a mirror.
        { binding: 10, visibility: GPUShaderStage.FRAGMENT, texture: { sampleType: 'float' } },
      ],
    });
    // EAGER, AND BEFORE THE GROUP THAT BINDS IT.
    //
    // Every other target here is made in `resize`, because every other target
    // is the size of the window. This one is not — it covers a fixed number of
    // METRES — and `resize` runs after the constructor, so building it there
    // left `vHeight` undefined at the moment the globals group asked for it.
    this.height = device.createTexture({
      size: [CAUSTIC_MAP_X, CAUSTIC_MAP_Z],
      format: DEPTH_FORMAT,
      usage: GPUTextureUsage.RENDER_ATTACHMENT | GPUTextureUsage.TEXTURE_BINDING,
      label: 'height',
    });
    this.vHeight = this.height.createView();
    // AND THE CAUSTIC MAP, FOR THE SAME REASON THE HEIGHT MAP IS HERE.
    //
    // It was built lazily in `resize`, which was fine while only the post pass
    // read it — post's bind group is rebuilt on every resize. It is in the
    // SHARED globals group now, and that group is built once, in this
    // constructor, before `resize` has ever run. Lazy would hand it `undefined`.
    this.caustic = device.createTexture({
      size: [CAUSTIC_MAP_X, CAUSTIC_MAP_Z],
      format: 'r16float',
      usage: GPUTextureUsage.RENDER_ATTACHMENT | GPUTextureUsage.TEXTURE_BINDING,
      label: 'caustic',
    });
    this.vCaustic = this.caustic.createView();

    this.globalsBind = device.createBindGroup({
      layout: this.globalsLayout,
      entries: [
        { binding: 0, resource: { buffer: this.globalsBuf } },
        { binding: 1, resource: { buffer: this.lightsBuf } },
        { binding: 2, resource: { buffer: this.lightCountBuf } },
        { binding: 3, resource: detail.sampler },
        { binding: 4, resource: detail.view },
        { binding: 5, resource: { buffer: this.coneBuf } },
        { binding: 6, resource: this.envSampler },
        { binding: 7, resource: this.envTex.createView({ dimension: '2d-array' }) },
        { binding: 8, resource: this.vHeight },
        { binding: 9, resource: this.vCaustic },
        { binding: 10, resource: this.studioTex.createView() },
      ],
    });

    // --- terrain ----------------------------------------------------------
    const layerLayout = device.createBindGroupLayout({
      label: 'layer',
      entries: [{ binding: 0, visibility: GPUShaderStage.FRAGMENT, buffer: { type: 'uniform' } }],
    });
    // `params.x` is the LAYER INDEX, and until now it was zero for all three.
    // The shader has always documented it as the index — `fsTerrain` needs to
    // know whether it is drawing the ground the player is flying over or a
    // silhouette 190 m behind it — but nothing ever wrote one, so the field was
    // a comment describing an intention rather than a value.
    // The buffers are KEPT now, not dropped on the floor after one write. A
    // layer's tint and defocus used to be constants — correct while the camera
    // could only ever sit in front of layer 0 — but a sub that can fly into a
    // lane needs the lane it is in to be the sharp one, and that is a fact
    // about distance, not about an array index.
    LAYERS.forEach((L, li) => {
      const buf = makeUniform(device, 32, 'layer');
      device.queue.writeBuffer(buf, 0, new Float32Array([...L.tint, 1, li, 1, L.defocus, 0]));
      this.layerBufs.push(buf);
      this.terrainBinds.push(
        device.createBindGroup({ layout: layerLayout, entries: [{ binding: 0, resource: { buffer: buf } }] }),
      );
    });
    this.terrainPipe = device.createRenderPipeline({
      label: 'terrain',
      layout: device.createPipelineLayout({ bindGroupLayouts: [this.globalsLayout, layerLayout] }),
      vertex: { module: sceneModule, entryPoint: 'vsTerrain', buffers: [TERRAIN_VERTEX_LAYOUT] },
      fragment: { module: sceneModule, entryPoint: 'fsTerrain', targets: sceneTargets() },
      primitive: { topology: 'triangle-list', cullMode: 'none' },
      depthStencil: { format: DEPTH_FORMAT, depthWriteEnabled: true, depthCompare: 'less' },
      multisample: { count: this.samples },
    });


    // --- instanced meshes --------------------------------------------------
    this.meshLayout = device.createBindGroupLayout({
      label: 'instances',
      entries: [{ binding: 2, visibility: GPUShaderStage.VERTEX, buffer: { type: 'read-only-storage' } }],
    });
    this.meshPipe = device.createRenderPipeline({
      label: 'mesh',
      layout: device.createPipelineLayout({ bindGroupLayouts: [this.globalsLayout, this.meshLayout] }),
      vertex: { module: sceneModule, entryPoint: 'vsMesh', buffers: [MESH_VERTEX_LAYOUT] },
      fragment: { module: sceneModule, entryPoint: 'fsMesh', targets: sceneTargets() },
      primitive: { topology: 'triangle-list', cullMode: 'none' },
      depthStencil: { format: DEPTH_FORMAT, depthWriteEnabled: true, depthCompare: 'less' },
      multisample: { count: this.samples },
    });

    // --- translucent meshes (jellyfish) ------------------------------------
    // Alpha blended, depth tested but not written, so overlapping tissue
    // accumulates instead of z-fighting itself.
    // THE HEIGHT MAP'S TWO PIPELINES. No fragment stage at all: a depth-only
    // pass wants a position and nothing else, which is most of why a whole
    // second view of the scene is affordable. No shading, no lighting, no
    // material, one attachment, 256 x 256.
    const heightLayout = device.createBindGroupLayout({
      label: 'height:globals',
      entries: [{
        binding: 0,
        visibility: GPUShaderStage.VERTEX,
        buffer: { type: 'uniform' },
      }],
    });
    this.heightBind = device.createBindGroup({
      layout: heightLayout,
      entries: [{ binding: 0, resource: { buffer: this.globalsBuf } }],
    });
    this.heightTerrainPipe = device.createRenderPipeline({
      label: 'height:terrain',
      layout: device.createPipelineLayout({ bindGroupLayouts: [heightLayout] }),
      vertex: { module: sceneModule, entryPoint: 'vsTerrainH', buffers: [TERRAIN_VERTEX_LAYOUT] },
      primitive: { topology: 'triangle-list', cullMode: 'none' },
      depthStencil: { format: DEPTH_FORMAT, depthWriteEnabled: true, depthCompare: 'less' },
    });
    this.heightMeshPipe = device.createRenderPipeline({
      label: 'height:mesh',
      layout: device.createPipelineLayout({
        bindGroupLayouts: [heightLayout, this.meshLayout],
      }),
      vertex: { module: sceneModule, entryPoint: 'vsMeshH', buffers: [MESH_VERTEX_LAYOUT] },
      primitive: { topology: 'triangle-list', cullMode: 'none' },
      depthStencil: { format: DEPTH_FORMAT, depthWriteEnabled: true, depthCompare: 'less' },
    });
    /**
     * GROUP 2 IS THE BACKDROP, and only this pipeline has it.
     *
     * `jellyPipe` takes an EXPLICIT pipeline layout rather than 'auto', so a
     * group the shader reads has to be declared here — adding the binding to
     * `fsJelly` alone gets "the entry-point uses bindings in group 2 but the
     * PipelineLayout doesn't have a BindGroupLayout for this index", which is
     * what it did. Declared as its own layout so the mesh and terrain
     * pipelines, which share `globalsLayout` and `meshLayout`, are untouched.
     */
    this.backdropLayout = device.createBindGroupLayout({
      label: 'backdrop',
      entries: [
        { binding: 0, visibility: GPUShaderStage.FRAGMENT, sampler: { type: 'filtering' } },
        { binding: 1, visibility: GPUShaderStage.FRAGMENT, texture: { sampleType: 'float' } },
      ],
    });
    /**
     * GLASS. The same pipeline as the jellyfish in every respect that matters —
     * premultiplied blend, depth tested and not written, both faces drawn, the
     * material buffer preserved by a max on alpha — and a different pair of
     * entry points.
     *
     * Sharing `jellyPipe` outright was the first attempt and it fails at the
     * vertex stage: `vsJelly` deforms by material band and a pane lands in the
     * tentacle branch. See `vsGlass`.
     */
    const glassPipeDesc = (label: string, vs: string, fs: string) =>
      device.createRenderPipeline({
        label,
        layout: device.createPipelineLayout({
          bindGroupLayouts: [this.globalsLayout, this.meshLayout, this.backdropLayout],
        }),
        vertex: { module: sceneModule, entryPoint: vs, buffers: [MESH_VERTEX_LAYOUT] },
        fragment: {
          module: sceneModule,
          entryPoint: fs,
          targets: sceneTargets(
            {
              blend: {
                color: { srcFactor: 'one', dstFactor: 'one-minus-src-alpha', operation: 'add' },
                alpha: { srcFactor: 'zero', dstFactor: 'one-minus-src-alpha', operation: 'add' },
              },
            },
            true,
            {
              writeMask: GPUColorWrite.ALPHA,
              blend: {
                color: { srcFactor: 'zero', dstFactor: 'one', operation: 'add' },
                alpha: { srcFactor: 'one', dstFactor: 'one', operation: 'max' },
              },
            },
          ),
        },
        primitive: { topology: 'triangle-list', cullMode: 'none' },
        depthStencil: { format: DEPTH_FORMAT, depthWriteEnabled: false, depthCompare: 'less' },
        multisample: { count: this.samples },
      });
    this.glassPipe = glassPipeDesc('glass', 'vsGlass', 'fsGlass');

    this.jellyPipe = device.createRenderPipeline({
      label: 'jelly',
      layout: device.createPipelineLayout({
        bindGroupLayouts: [this.globalsLayout, this.meshLayout, this.backdropLayout],
      }),
      vertex: { module: sceneModule, entryPoint: 'vsJelly', buffers: [MESH_VERTEX_LAYOUT] },
      fragment: {
        module: sceneModule,
        entryPoint: 'fsJelly',
        targets: sceneTargets(
          {
            blend: {
              color: { srcFactor: 'one', dstFactor: 'one-minus-src-alpha', operation: 'add' },
            // Alpha is the defocus channel, not opacity — the colour factors
            // above read `src.a` straight from the shader, so what happens here
            // changes nothing about how the tissue blends. A jellyfish is in
            // the play layer and sharp; it has no depth of its own to be
            // judged by, so it writes NOTHING.
            //
            // WEIGHTED BY COVERAGE, which is the version that is right in both
            // directions. `zero, zero` WIPED the channel — every translucent
            // fragment reset the focus of whatever was behind it, however
            // faint. `zero, one` left it alone and broke the other half: a
            // jellyfish hanging in front of a parallax lane inherited the
            // LANE's defocus, so the play layer's own animals started
            // gathering into big soft coins.
            //
            // `one-minus-src-alpha` says what was always meant: a fragment
            // pulls the channel toward sharp exactly as far as it covers the
            // pixel. Solid tissue is sharp, a 2% speck moves nothing.
              alpha: { srcFactor: 'zero', dstFactor: 'one-minus-src-alpha', operation: 'add' },
            },
          },
          true,
          {
            // Preserve the opaque material behind translucent tissue. The old
            // full MRF replacement deleted terrain reflections under every
            // bell, producing the closed black contours seen through water.
            // Only alpha carries the tiny jelly/foam classification tag; max
            // also preserves a real glass flag already present behind it.
            writeMask: GPUColorWrite.ALPHA,
            blend: {
              color: { srcFactor: 'zero', dstFactor: 'one', operation: 'add' },
              alpha: { srcFactor: 'one', dstFactor: 'one', operation: 'max' },
            },
          },
        ),
      },
      primitive: { topology: 'triangle-list', cullMode: 'none' },
      depthStencil: { format: DEPTH_FORMAT, depthWriteEnabled: false, depthCompare: 'less' },
      multisample: { count: this.samples },
    });

    // --- particles ---------------------------------------------------------
    const partLayout = device.createBindGroupLayout({
      label: 'particle-read',
      entries: [{ binding: 0, visibility: GPUShaderStage.VERTEX, buffer: { type: 'read-only-storage' } }],
    });
    this.particleBind = device.createBindGroup({
      layout: partLayout,
      entries: [{ binding: 0, resource: { buffer: this.particles.buffer } }],
    });
    const particleDesc = (aux: boolean): GPURenderPipelineDescriptor => ({
      label: aux ? 'particles:aux' : 'particles',
      layout: device.createPipelineLayout({ bindGroupLayouts: [this.globalsLayout, partLayout] }),
      vertex: { module: this.particles.renderModule, entryPoint: 'vsParticle' },
      fragment: {
        module: this.particles.renderModule,
        entryPoint: 'fsParticle',
        // Premultiplied alpha: glow == 1 behaves additively, glow == 0 blends.
        // Alpha carries defocus rather than opacity; see the jelly pipeline.
        //
        // COLOUR ONLY — see `sceneTargets`. A speck of drifting silt is not a
        // surface and does not move the scene behind it.
        targets: sceneTargets({
          blend: {
            color: { srcFactor: 'one', dstFactor: 'one-minus-src-alpha', operation: 'add' },
            // AND THE SAME HERE, coverage-weighted for the same reason.
            //
            // `fsParticle` returns `a * (1 - glow)`, so an additive mote — a
            // spark, a bioluminescent bloom — has src.a == 0 and moves this
            // channel not at all, which is correct: it is a light, not a
            // surface, and it occludes nothing. Silt, which does blend, pulls
            // the focus toward sharp by however much of the pixel it covers.
            alpha: { srcFactor: 'zero', dstFactor: 'one-minus-src-alpha', operation: 'add' },
          },
        }, aux),
      },
      primitive: { topology: 'triangle-list', cullMode: 'none' },
      depthStencil: { format: DEPTH_FORMAT, depthWriteEnabled: false, depthCompare: 'less' },
      multisample: { count: this.samples },
    });
    this.particlePipe = device.createRenderPipeline(particleDesc(false));
    this.particlePipeAux = device.createRenderPipeline(particleDesc(true));

    // Single-sampled: it draws into the swapchain after every resolve has
    // happened, so there is no MSAA left to match.
    this.text = new TextBatch(device, gpu.format);

    // --- post --------------------------------------------------------------
    this.postSampler = device.createSampler({
      magFilter: 'linear', minFilter: 'linear',
      addressModeU: 'clamp-to-edge', addressModeV: 'clamp-to-edge',
    });
    this.postBuf = makeUniform(device, this.postData.byteLength, 'post');

    this.brightPipe = device.createRenderPipeline({
      label: 'bright',
      layout: 'auto',
      vertex: { module: postModule, entryPoint: 'vsFull' },
      fragment: { module: postModule, entryPoint: 'fsBright', targets: [{ format: HDR_FORMAT }] },
      primitive: { topology: 'triangle-list' },
    });
    this.blurPipe = device.createRenderPipeline({
      label: 'blur',
      layout: 'auto',
      vertex: { module: postModule, entryPoint: 'vsFull' },
      fragment: { module: postModule, entryPoint: 'fsBlur', targets: [{ format: HDR_FORMAT }] },
      primitive: { topology: 'triangle-list' },
    });
    // Half-resolution, and its own pass rather than a branch inside the
    // composite: the whole point is to run it over a quarter of the pixels, and
    // a fragment shader cannot choose its own resolution.
    // THE CAUSTIC SPLAT. A point list with an additive blend, which is the
    // whole of the accumulation — no atomics, no storage buffer, no compute.
    // Each point works out where its share of the sun lands and is drawn there.
    this.causticPipe = device.createRenderPipeline({
      label: 'caustic',
      layout: 'auto',
      vertex: { module: postModule, entryPoint: 'vsCaustic' },
      fragment: {
        module: postModule,
        entryPoint: 'fsCaustic',
        targets: [{
          format: 'r16float',
          blend: {
            color: { srcFactor: 'one', dstFactor: 'one', operation: 'add' },
            alpha: { srcFactor: 'one', dstFactor: 'one', operation: 'add' },
          },
        }],
      },
      primitive: { topology: 'point-list' },
    });
    this.volPipe = device.createRenderPipeline({
      label: 'volumetric',
      layout: 'auto',
      vertex: { module: postModule, entryPoint: 'vsFull' },
      fragment: { module: postModule, entryPoint: 'fsVolumetric', targets: [{ format: HDR_FORMAT }] },
      primitive: { topology: 'triangle-list' },
    });
    this.dofPipe = device.createRenderPipeline({
      label: 'dof',
      layout: 'auto',
      vertex: { module: postModule, entryPoint: 'vsFull' },
      fragment: { module: postModule, entryPoint: 'fsDof', targets: [{ format: HDR_FORMAT }] },
      primitive: { topology: 'triangle-list' },
    });
    if (this.taa) {
      this.taaPipe = device.createRenderPipeline({
        label: 'taa',
        layout: 'auto',
        vertex: { module: postModule, entryPoint: 'vsFull' },
        fragment: { module: postModule, entryPoint: 'fsTaa', targets: [{ format: gpu.format }] },
        primitive: { topology: 'triangle-list' },
      });
    }
    if (this.fxaa) {
      this.fxaaPipe = device.createRenderPipeline({
        label: 'fxaa',
        layout: 'auto',
        vertex: { module: postModule, entryPoint: 'vsFull' },
        fragment: { module: postModule, entryPoint: 'fsFxaa', targets: [{ format: gpu.format }] },
        primitive: { topology: 'triangle-list' },
      });
    }
    // Sharpening, which is always the LAST thing that touches a pixel — it is
    // there to put back what TAA, the half-resolution volumetric and FXAA all
    // take out, so it has to come after every one of them.
    this.sharpenPipe = device.createRenderPipeline({
      label: 'sharpen',
      layout: 'auto',
      vertex: { module: postModule, entryPoint: 'vsFull' },
      fragment: { module: postModule, entryPoint: 'fsSharpen', targets: [{ format: gpu.format }] },
      primitive: { topology: 'triangle-list' },
    });

    this.compositePipe = device.createRenderPipeline({
      label: 'composite',
      layout: 'auto',
      vertex: { module: postModule, entryPoint: 'vsFull' },
      fragment: { module: postModule, entryPoint: 'fsComposite', targets: [{ format: gpu.format }] },
      primitive: { topology: 'triangle-list' },
    });
    for (let i = 0; i < quality.blurPasses; i++) {
      const b = makeUniform(device, 16, `blur${i}`);
      this.blurBufs.push(b);
      this.blurUniformBinds.push(
        device.createBindGroup({
          layout: this.blurPipe.getBindGroupLayout(1),
          entries: [{ binding: 0, resource: { buffer: b } }],
        }),
      );
    }

    // Allocated once. Two queries, one resolve target, two staging buffers to
    // cycle so a readback never has to wait on the frame that produced it.
    if (gpu.timestamps) {
      this.tsSet = device.createQuerySet({ type: 'timestamp', count: TS_QUERIES });
      this.tsResolve = device.createBuffer({
        size: TS_QUERIES * 8, usage: GPUBufferUsage.QUERY_RESOLVE | GPUBufferUsage.COPY_SRC,
      });
      this.tsRead = [0, 1].map(() => device.createBuffer({
        size: TS_QUERIES * 8, usage: GPUBufferUsage.COPY_DST | GPUBufferUsage.MAP_READ,
      }));
      this.tsFlight = this.tsRead.map(() => null);
      const nopMod = device.createShaderModule({
        label: 'ts-nop',
        code: `@vertex fn vs(@builtin(vertex_index) i : u32) -> @builtin(position) vec4<f32> {
  var p = array(vec2<f32>(-1.0, -1.0), vec2<f32>(3.0, -1.0), vec2<f32>(-1.0, 3.0));
  return vec4<f32>(p[i], 0.0, 1.0);
}
@fragment fn fs() -> @location(0) vec4<f32> { return vec4<f32>(0.0, 0.0, 0.0, 1.0); }`,
      });
      this.tsNop = device.createRenderPipeline({
        label: 'ts-nop',
        layout: 'auto',
        vertex: { module: nopMod, entryPoint: 'vs' },
        fragment: { module: nopMod, entryPoint: 'fs', targets: [{ format: 'rgba8unorm' }] },
        primitive: { topology: 'triangle-list' },
      });
      this.tsNopView = device.createTexture({
        label: 'ts-nop',
        size: [1, 1], format: 'rgba8unorm',
        usage: GPUTextureUsage.RENDER_ATTACHMENT,
      }).createView();
    }
    this.resize();
  }

  /**
   * Claim a timestamp pair for one pass, or nothing when timing is off.
   *
   * Returned as the `timestampWrites` a pass descriptor takes, so a call site
   * spends one spread and does not have to know an index exists. The label is
   * pushed in the same order the indices are claimed, which is what lets the
   * readback — two frames later, against a pass list that may since have
   * changed — put the right name on the right number.
   */
  private tsPass(label: string) {
    const q = this.tsSet;
    if (!q || !this.gpuPassTiming || this.tsUsed + 1 > TS_QUERIES) return undefined;
    const i = this.tsUsed++;
    this.tsName(label);
    return { querySet: q, endOfPassWriteIndex: i };
  }

  /**
   * Record a label, refusing a duplicate within one frame.
   *
   * TWO PASSES SHARING A NAME IS SILENTLY WRONG, not loudly. The readback
   * smooths into `gpuPass[name]` per pair, so a second pair with the same label
   * does not add to the first — it lands on top of it, and the breakdown shows
   * one of the two while claiming to show the work of both. Today the only
   * repeated label is `composite`, whose three call sites are mutually
   * exclusive branches; this exists so that stops being a fact somebody has to
   * re-verify every time a branch is added.
   */
  private tsName(label: string) {
    if (this.tsNames.includes(label)) {
      if (!this.tsDupWarned.has(label)) {
        this.tsDupWarned.add(label);
        console.warn(`[abyss] two GPU passes labelled "${label}" in one frame; ` +
                     'the breakdown will show one of them and hide the other');
      }
      // Suffixed rather than dropped: a number under a slightly odd name beats
      // a pass that silently vanishes from a profile.
      let n = 2;
      while (this.tsNames.includes(`${label}#${n}`)) n++;
      this.tsNames.push(`${label}#${n}`);
      return;
    }
    this.tsNames.push(label);
  }

  /**
   * Time a REGION of the encoder rather than a single pass, by bracketing it
   * with two empty compute passes.
   *
   * `writeTimestamp` was dropped from the spec in favour of `timestampWrites`
   * on a pass descriptor, so this is the only way to time work that is encoded
   * by somebody else — the wake solver and the particle simulation each own
   * their passes, and threading a query index down into them would put this
   * profiler's plumbing into two modules that have no other reason to know it
   * exists. It is the same trick the whole-frame bracket uses.
   *
   * Returns the closing half. Calling it is not optional: skip it and the
   * label is claimed with no end timestamp written, and the readback pairs it
   * with whatever the query set happened to hold.
   */
  /**
   * Encode a pass whose only purpose is to write one timestamp.
   *
   * The dispatch is the point — see `tsNop`. Every bracket in this file goes
   * through here so that none of them can be written as an empty pass again.
   */
  private tsMark(enc: GPUCommandEncoder, label: string,
                 writes: GPURenderPassTimestampWrites) {
    if (!this.tsNop || !this.tsNopView) return;
    const pass = enc.beginRenderPass({
      label,
      colorAttachments: [{
        view: this.tsNopView, loadOp: 'clear', storeOp: 'store',
        clearValue: { r: 0, g: 0, b: 0, a: 1 },
      }],
      timestampWrites: writes,
    });
    pass.setPipeline(this.tsNop);
    pass.draw(3);
    pass.end();
  }

  private tsRegion(enc: GPUCommandEncoder, label: string): () => void {
    const q = this.tsSet;
    if (!q || !this._gpuPassTiming || this.tsUsed + 2 > TS_QUERIES) return () => {};
    // A boundary mark first, so the region's cost is measured from HERE rather
    // than from whatever pass happened to precede it. Its own slot carries the
    // empty label, which means "advance the timeline, report nothing".
    this.tsUsed++;
    this.tsNames.push('');
    this.tsMark(enc, `ts:${label}:in`, { querySet: q, endOfPassWriteIndex: this.tsUsed - 1 });
    const end = this.tsUsed++;
    this.tsName(label);
    return () => {
      this.tsMark(enc, `ts:${label}`, { querySet: q, endOfPassWriteIndex: end });
    };
  }

  // -------------------------------------------------------------------------

  registerMesh(
    name: string, data: MeshData, capacity: number,
    translucent = false, glass = false,
  ) {
    // Last frame's transforms, by index. Carried inside the renderer so that no
    // caller has to know that temporal antialiasing exists.
    const prev = new Float32Array(capacity * 16);
    // Re-registering a slot used to strand the old mesh's two vertex buffers
    // and its instance buffer on the device with no way to reach them. World's
    // constructor makes ~40 slots, so a mode switch that rebuilt them would
    // leak the whole set every time.
    const old = this.slots.get(name);
    if (old) {
      old.mesh.vbo.destroy();
      old.mesh.ibo.destroy();
      old.buffer.destroy();
    }

    const mesh = uploadMesh(this.device, data, name);
    const buffer = this.device.createBuffer({
      size: Math.max(256, capacity * INSTANCE_FLOATS * 4),
      usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_DST,
      label: `${name}:inst`,
    });
    const bind = this.device.createBindGroup({
      layout: this.meshLayout,
      entries: [{ binding: 2, resource: { buffer } }],
    });
    const refractHalf = translucent ? meshHalfExtents(data) : [0, 0, 0] as [number, number, number];
    let refractMaxV = 0;
    if (translucent) {
      for (let i = 7; i < data.vertices.length; i += VERT_FLOATS) {
        refractMaxV = Math.max(refractMaxV, Math.abs(data.vertices[i]!));
      }
    }
    this.slots.set(name, {
      mesh, capacity, buffer, bind, prev, count: 0, translucent, glass,
      refractHalf, refractMaxV,
      data: new Float32Array(capacity * INSTANCE_FLOATS),
    });
  }

  /**
   * Record one instance slot's contribution, keeping the eight heaviest. Eight
   * because that is what fits on a phone screen next to the rest of the panel,
   * and because in practice two slots are always most of the frame.
   */
  private noteSlot(name: string, slot: MeshSlot) {
    const tris = (slot.mesh.indexCount / 3) * slot.count;
    this.stats.instTris += tris;
    this.stats.instDraws++;
    // Ranked by how FULL the slot is, not by how heavy it is.
    //
    // Triangles name the slot costing the most time; occupancy names the slot
    // about to start dropping instances, and those are different questions.
    // The second one had no readout at all, which is why a slot at 99% and a
    // slot at 4% looked identical right up to the point where scenery began
    // vanishing.
    const top = this.stats.top;
    const use = slot.count / Math.max(1, slot.capacity);
    if (top.length < 8 || use > top[top.length - 1][3]) {
      top.push([name, tris, slot.count, use, slot.capacity]);
      top.sort((a, b) => b[3] - a[3]);
      if (top.length > 8) top.length = 8;
    }
  }

  /**
   * Conservative screen bounds for one refractive instance.
   *
   * The vertex shader animates every translucent mesh through `vsJelly`, so a
   * static mesh AABB is not enough. These three half-extents bound every branch
   * of that animation: bell squeeze/flare, bloom expansion and skirt lag, and
   * tentacle motion (whose only instance-dependent amplitude is params.z).
   * The resulting local box is transformed into a world AABB, then clipped in
   * homogeneous space with the exact jittered matrix the shader uses.
   */
  private refractiveInstanceRect(
    slot: MeshSlot, o: number, viewProj: Mat4,
    width: number, height: number, padX: number, padY: number,
  ): PixelRect | null | 'full' {
    const d = slot.data;
    /**
     * GLASS DOES NOT MOVE, SO IT DOES NOT GET THE ANIMATION PADDING.
     *
     * Everything below exists because `vsJelly` deforms its meshes and a static
     * AABB cannot bound that: the bell squeezes, the bloom flares, the skirt
     * lags and the tentacles throw, so the rect has to cover every branch. The
     * vertical term alone inflates by HALF.
     *
     * A window pane is rigid — `vsGlass` was written precisely so it would not
     * ripple — so all of that is pure waste on the slots that now dominate the
     * backdrop. With seven modules on screen their padded rects merge into one
     * region, and the measured copy was 58 percent of the frame.
     */
    const v = slot.glass ? 0 : slot.refractMaxV;
    const v2 = v * v;
    // Rigid geometry takes the mesh's own extents; only the animated meshes pay
    // for the branches of their animation.
    const waveScale = slot.glass ? 1.0 : Math.max(1.16 + v2 * 0.098, 1.13);
    const heightScale = slot.glass ? 1.0 : 1.5;
    const tentacle = slot.glass ? 0 : v2 * Math.abs(0.5 + d[o + 42]! * 0.4);
    const lx = slot.refractHalf[0] * waveScale + v2 * 0.22 + tentacle + 1e-3;
    const ly = slot.refractHalf[1] * heightScale + v * 0.62 + 1e-3;
    const lz = slot.refractHalf[2] * waveScale + tentacle * 0.5 + 1e-3;

    // An affine transform maps a local AABB to this conservative world AABB.
    const wx = Math.abs(d[o]!) * lx + Math.abs(d[o + 4]!) * ly + Math.abs(d[o + 8]!) * lz;
    const wy = Math.abs(d[o + 1]!) * lx + Math.abs(d[o + 5]!) * ly + Math.abs(d[o + 9]!) * lz;
    const wz = Math.abs(d[o + 2]!) * lx + Math.abs(d[o + 6]!) * ly + Math.abs(d[o + 10]!) * lz;
    const tx = d[o + 12]!, ty = d[o + 13]!, tz = d[o + 14]!;

    let outsideLeft = true, outsideRight = true;
    let outsideBottom = true, outsideTop = true;
    let outsideNear = true, outsideFar = true;
    let positiveW = true;
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;

    for (let iz = -1; iz <= 1; iz += 2) {
      for (let iy = -1; iy <= 1; iy += 2) {
        for (let ix = -1; ix <= 1; ix += 2) {
          const x = tx + ix * wx, y = ty + iy * wy, z = tz + iz * wz;
          const cx = viewProj[0] * x + viewProj[4] * y + viewProj[8] * z + viewProj[12];
          const cy = viewProj[1] * x + viewProj[5] * y + viewProj[9] * z + viewProj[13];
          const cz = viewProj[2] * x + viewProj[6] * y + viewProj[10] * z + viewProj[14];
          const cw = viewProj[3] * x + viewProj[7] * y + viewProj[11] * z + viewProj[15];
          outsideLeft &&= cx < -cw; outsideRight &&= cx > cw;
          outsideBottom &&= cy < -cw; outsideTop &&= cy > cw;
          outsideNear &&= cz < 0; outsideFar &&= cz > cw;
          if (!(cw > 1e-5) || !Number.isFinite(cx + cy + cz + cw)) {
            positiveW = false;
          } else {
            const nx = cx / cw, ny = cy / cw;
            minX = Math.min(minX, nx); maxX = Math.max(maxX, nx);
            minY = Math.min(minY, ny); maxY = Math.max(maxY, ny);
          }
        }
      }
    }

    // A box wholly outside any homogeneous clip plane cannot rasterise.
    if (outsideLeft || outsideRight || outsideBottom || outsideTop || outsideNear || outsideFar) {
      return null;
    }
    // A box crossing the eye/near plane can project without a finite bound.
    // It is rare and a full copy is the only conservative answer.
    if (!positiveW) return 'full';

    let x0 = Math.floor((Math.max(-1, minX) * 0.5 + 0.5) * width) - padX;
    let x1 = Math.ceil((Math.min(1, maxX) * 0.5 + 0.5) * width) + padX;
    // NDC +Y is the top of a WebGPU viewport; texture rows grow downward.
    let y0 = Math.floor((1 - Math.min(1, maxY)) * 0.5 * height) - padY;
    let y1 = Math.ceil((1 - Math.max(-1, minY)) * 0.5 * height) + padY;
    const a = BACKDROP_COPY_ALIGNMENT;
    x0 = Math.max(0, Math.floor(x0 / a) * a);
    y0 = Math.max(0, Math.floor(y0 / a) * a);
    x1 = Math.min(width, Math.ceil(x1 / a) * a);
    y1 = Math.min(height, Math.ceil(y1 / a) * a);
    return x1 > x0 && y1 > y0 ? { x0, y0, x1, y1 } : null;
  }

  /** Regions of this frame's opaque HDR image that refraction may sample. */
  private backdropRegions(p: FrameParams): PixelRect[] {
    const width = this.hdr.width, height = this.hdr.height;
    const full: PixelRect = { x0: 0, y0: 0, x1: width, y1: height };
    if (!this.probe.backdropBounds) return [full];
    const rects: PixelRect[] = [];
    /**
     * HOW FAR OUTSIDE ITS OWN SILHOUETTE A REFRACTIVE SURFACE CAN READ.
     *
     * The backdrop is copied in RECTANGLES, not whole-frame, so anything that
     * samples it beyond a copied rect gets whatever was in that texture before
     * — a stale frame, or nothing. That shows up as coloured fringes hugging
     * the edge of a pane, which is exactly what the habitat windows were doing.
     *
     * 0.055 was right when `refractDelta` was the only reader: its offset is
     * bounded by `strength * 0.055` per axis. `glassRefract` is not bounded by
     * that. Its offset is `dev * thick * chord * k * 2`, and while `dev` peaks
     * at grazing where `chord` vanishes, their PRODUCT peaks around 0.085 near
     * 45 degrees — so with `thick` 0.55 and the doubling for two surfaces the
     * bound is about 0.094 per unit of strength, not 0.055.
     *
     * Taking the max of the two keeps the jellyfish rects exactly as they were
     * and widens them only when the glass dial is turned up.
     */
    const GLASS_UV_BOUND = 0.094;
    const reach = Math.abs(this.seaTune.refraction)
      * Math.max(0.055, GLASS_UV_BOUND * Math.max(0, this.seaTune.glassRefract));
    const padX = Math.ceil(reach * width) + 2;
    const padY = Math.ceil(reach * height) + 2;

    for (const slot of this.slots.values()) {
      if (!slot.translucent || !slot.count) continue;
      /**
       * GLASS ONLY, UNLESS THE JELLIES ARE EXPLICITLY ASKED FOR.
       *
       * This is where refraction actually costs something. The shader term is
       * one texture fetch; the EXPENSE is the backdrop copy, and its area is
       * the union of these rects. Jellyfish are the worst possible contributor
       * to that: they arrive in schools, and `refractiveInstanceRect` has to
       * pad each one for every branch of its vertex animation — bell squeeze,
       * bloom flare, skirt lag, tentacle throw — so a dozen animals can cover
       * most of the frame between them.
       *
       * What that buys is a wobble on tissue thin enough to be nearly
       * invisible. A window pane is the opposite trade: a handful of static,
       * tightly-bounded rects, and the effect is what says there is glass in
       * the opening at all.
       */
      if (!slot.glass && !this.probe.jellyRefraction) continue;
      // The debug freeze deliberately leaves old transforms in GPU memory.
      // Their CPU positions are no longer authoritative, so keep the old full
      // copy while it is active instead of cropping against the wrong frame.
      if (!this.probe.instanceUpload) return [full];
      for (let ii = 0; ii < slot.count; ii++) {
        const r = this.refractiveInstanceRect(
          slot, ii * INSTANCE_FLOATS, p.viewProj, width, height, padX, padY,
        );
        if (r === 'full') return [full];
        if (!r) continue;
        addBackdropRegion(rects, r);
        // Keep the CPU work bounded in pathological schools. Coverage remains
        // conservative; only empty space between rectangles is added.
        if (rects.length > MAX_BACKDROP_REGIONS * 2) capBackdropRegions(rects);
        const covered = rects.reduce((sum, q) => sum + rectArea(q), 0);
        if (covered >= width * height * 0.9) return [full];
      }
    }
    capBackdropRegions(rects);
    const covered = rects.reduce((sum, r) => sum + rectArea(r), 0);
    return covered >= width * height * 0.9 ? [full] : rects;
  }

  /**
   * Tint and focus per layer, from where the camera IS rather than from which
   * slot the layer sits in.
   *
   * The authored `defocus` and `tint` describe layer 1 and 2 as seen from in
   * front of layer 0, and that is still exactly what comes out while the sub is
   * in the near lane. Fly it back and the roles swap: the lane the camera is
   * level with resolves, and the one it left goes soft in front of it. Distance
   * is measured to the layer's near face, so a lane the camera is inside reads
   * as zero rather than as half its own thickness.
   */
  private writeLayers(p: FrameParams) {
    // What "one layer away" is worth, taken from the authored pair so the look
    // is unchanged at the camera position the game has always had.
    const REF = Math.abs(LAYERS[0]!.zFront - LAYERS[1]!.zFront) || 55;
    for (let li = 0; li < LAYERS.length; li++) {
      const L = LAYERS[li]!;
      const away = Math.max(0, Math.abs(p.focusZ - L.zFront) - 20) / REF;
      // Approaching a layer softens it as well, so it is already a blur by the
      // time it is dropped rather than a sharp wall that vanishes.
      const vis = layerVisibility(p.camZ, L.zFront);
      const defocus = Math.min(1, Math.max(away * 0.62, 1 - vis));
      // Tint fades toward neutral as a layer becomes the one being flown in.
      const k = Math.min(1, away) * this.seaTune.laneTint;
      const s = this.layerScratch;
      s[0] = 1 + (L.tint[0] - 1) * k;
      s[1] = 1 + (L.tint[1] - 1) * k;
      s[2] = 1 + (L.tint[2] - 1) * k;
      s[3] = 1;
      s[4] = li;
      s[5] = 1;
      s[6] = defocus;
      s[7] = 0;
      this.device.queue.writeBuffer(this.layerBufs[li]!, 0, s);
    }
  }

  /** Queue one instance. `model` is copied immediately, so callers can reuse it. */
  queue(
    name: string,
    model: Mat4,
    tint: [number, number, number, number],
    emissive: [number, number, number, number],
    params?: [number, number, number, number],
    /** Second animation vector. x = body curvature; y/z/w spare. */
    params2?: [number, number, number, number],
    /**
     * PBR override: metallic, roughness, dielectric F0, enable.
     *
     * Omit it and the instance is shaded from its material tag exactly as it
     * always has been — the shader gates the whole block on the fourth
     * component. Pass one and this instance stops being "whatever slot 0.35
     * means" and starts being a material in its own right.
     */
    pbr?: [number, number, number, number],
  ) {
    const slot = this.slots.get(name);
    if (!slot || slot.count >= slot.capacity) {
      this.stats.slotOverflow++;
      // Named, so the fix is a number in one place rather than a hunt. Once per
      // slot per session: a full slot fills every frame it is full.
      if (!this.warned.has(name)) {
        this.warned.add(name);
        console.warn(`[abyss] instance slot '${name}' full at ${slot ? slot.capacity : 0}`
          + `${slot ? '' : ' (slot not registered)'}; instances are being dropped and will read as`
          + ` scenery vanishing. Raise instanceBudget on the quality tier.`);
      }
      return;
    }
    const o = slot.count * INSTANCE_FLOATS;
    slot.data.set(model, o);
    // Whatever stood at this index a frame ago. World draws its pools in a
    // fixed order, so index and entity line up for as long as the live set
    // holds; when it changes a few instances get a frame of the wrong history,
    // which the resolve's neighbourhood clamp throws away.
    slot.data.set(slot.prev.subarray(slot.count * 16, slot.count * 16 + 16), o + 16);
    slot.data[o + 32] = tint[0]; slot.data[o + 33] = tint[1];
    slot.data[o + 34] = tint[2]; slot.data[o + 35] = tint[3];
    slot.data[o + 36] = emissive[0]; slot.data[o + 37] = emissive[1];
    slot.data[o + 38] = emissive[2]; slot.data[o + 39] = emissive[3];
    slot.data[o + 40] = params ? params[0] : 0;
    slot.data[o + 41] = params ? params[1] : 1;
    slot.data[o + 42] = params ? params[2] : 0;
    slot.data[o + 43] = params ? params[3] : 0;
    slot.data[o + 44] = params2 ? params2[0] : 0;
    slot.data[o + 45] = params2 ? params2[1] : 0;
    slot.data[o + 46] = params2 ? params2[2] : 0;
    slot.data[o + 47] = params2 ? params2[3] : 0;
    slot.data[o + 48] = pbr ? pbr[0] : 0;
    slot.data[o + 49] = pbr ? pbr[1] : 0;
    slot.data[o + 50] = pbr ? pbr[2] : 0;
    slot.data[o + 51] = pbr ? pbr[3] : 0;
    slot.prev.set(model, slot.count * 16);
    slot.count++;
  }

  /**
   * `viewProjNJ` is the UNJITTERED matrix, and it has to be: TAA's sub-pixel
   * wobble is the signal the resolve accumulates, and a glyph edge riding on
   * it would shimmer — the one thing text must never do. Passed in rather than
   * read off the frame because the queue is reset before `render` is handed
   * one, which is the whole reason this method exists separately.
   */
  resetQueue(viewProjNJ: Mat4) {
    for (const s of this.slots.values()) s.count = 0;
    this.lightCount = 0;
    this.scatterLightCount = 0;
    this.text.begin(viewProjNJ, this.gpu.width, this.gpu.height, this.gpu.dpr,
                    this.probe.textSdf);
    const st = this.stats;
    st.terrainTris = 0; st.terrainDraws = 0;
    st.instTris = 0; st.instDraws = 0;
    st.particles = 0;
    st.slotOverflow = 0; st.lightOverflow = 0;
    st.backdropEnabled = false; st.backdropPixels = 0;
    st.backdropRegions = 0; st.backdropCoverage = 0;
    st.top.length = 0;
  }

  /** Upload the CPU-marched beam occlusion for this frame. */
  setConeShadow(data: Float32Array<ArrayBuffer>) {
    this.device.queue.writeBuffer(this.coneBuf, 0, data, 0, data.length);
  }

  addLight(
    x: number, y: number, z: number, range: number,
    r: number, g: number, b: number, intensity: number,
    /**
     * Whether the WATER around this light glows, not just the surfaces it
     * reaches. True for anything that makes its own light as a living thing —
     * a jellyfish, a bloom — and false for a lamp or an indicator, which is a
     * source in the medium too but is not what the effect is for.
     *
     * CARRIED IN THE SIGN OF `range`, which is otherwise always positive. The
     * instance block is eight floats and all eight are spoken for; a ninth
     * changes the stride for every light in a buffer of 192 and for the scene
     * shader that reads it, to say one bit. The same trick `params2.w` already
     * plays a few hundred lines away, for the same reason it was free there.
     */
    scatter = false,
  ) {
    if (this.lightCount >= this.lightCap) {
      this.stats.lightOverflow++;
      if (!this.warned.has('@lights')) {
        this.warned.add('@lights');
        console.warn(`[abyss] light budget full at ${this.lightCap}; further lights this frame are dropped. Raise maxLights on the quality tier.`);
      }
      return;
    }
    const o = this.lightCount * 8;
    this.lightsData[o] = x; this.lightsData[o + 1] = y;
    this.lightsData[o + 2] = z;
    this.lightsData[o + 3] = scatter ? -range : range;
    this.lightsData[o + 4] = r; this.lightsData[o + 5] = g;
    this.lightsData[o + 6] = b; this.lightsData[o + 7] = intensity;
    this.lightCount++;
    if (scatter) {
      const so = this.scatterLightCount * 8;
      this.scatterLightsData.set(this.lightsData.subarray(o, o + 8), so);
      this.scatterLightCount++;
    }
  }

  // -------------------------------------------------------------------------

  /**
   * One line for the debug overlay, because "is the panorama actually in use"
   * has now been asked three times and answered by running console commands.
   * A thing that can be looked at does not need to be interrogated.
   */
  get skyStats(): string {
    return `SKY  scatter + deck · cover ${this.cloudCover.toFixed(2)}`
      + `  gfx ${this.gfxSky}`;
  }

  resize() {
    const { device, width, height } = this.gpu;
    for (const t of [this.hdrMS, this.hdr, this.backdrop, this.depth, this.bloomA, this.bloomB,
      this.volTex, this.dofB, this.ldr, this.motion, this.mrf, this.mrfMS,
      ...this.history]) t?.destroy();
    this.hdrMS = undefined as unknown as GPUTexture;

    const msaa = this.samples > 1;
    /**
     * THE BACKDROP: the scene with the transmissive things NOT in it yet.
     *
     * Screen-space refraction has to sample what is BEHIND a surface, and
     * inside a single pass the colour target at a glass pixel already contains
     * the glass — an offset sample there reads neighbouring glass, not the
     * backdrop. So the scene pass splits after the opaque draws and the
     * translucent half samples this.
     *
     * With MSAA the split is nearly free: the first half already has to
     * resolve, so it resolves HERE and the second half resolves to `hdr` as
     * before. Without MSAA the renderer copies only the conservative regions
     * visible refractors can sample; see `backdropRegions`.
     */
    this.backdrop = device.createTexture({
      size: [width, height], format: HDR_FORMAT,
      usage: GPUTextureUsage.RENDER_ATTACHMENT | GPUTextureUsage.TEXTURE_BINDING
        | GPUTextureUsage.COPY_DST,
      label: 'backdrop',
    });
    this.vBackdrop = this.backdrop.createView();
    // Group 2 on the translucent pipeline. Rebuilt here because the texture is
    // window-sized and `resize` is the only place that knows the new one.
    this.backdropBind = device.createBindGroup({
      layout: this.backdropLayout,
      entries: [
        { binding: 0, resource: this.postSampler },
        { binding: 1, resource: this.vBackdrop },
      ],
      label: 'backdrop',
    });
    this.hdr = device.createTexture({
      size: [width, height], format: HDR_FORMAT,
      usage: GPUTextureUsage.RENDER_ATTACHMENT | GPUTextureUsage.TEXTURE_BINDING
        | GPUTextureUsage.COPY_SRC, label: 'hdr',
    });
    if (msaa) {
      this.hdrMS = device.createTexture({
        size: [width, height], format: HDR_FORMAT, sampleCount: this.samples,
        usage: GPUTextureUsage.RENDER_ATTACHMENT, label: 'hdr-ms',
      });
    }
    // Material G-buffer, same shape as the colour target and resolved the same
    // way — the composite has to SAMPLE it, and a multisampled texture cannot
    // be read as an ordinary one.
    this.mrf = device.createTexture({
      size: [width, height], format: MRF_FORMAT,
      usage: GPUTextureUsage.RENDER_ATTACHMENT | GPUTextureUsage.TEXTURE_BINDING, label: 'mrf',
    });
    if (msaa) {
      this.mrfMS = device.createTexture({
        size: [width, height], format: MRF_FORMAT, sampleCount: this.samples,
        usage: GPUTextureUsage.RENDER_ATTACHMENT, label: 'mrf-ms',
      });
    }
    this.depth = device.createTexture({
      size: [width, height], format: DEPTH_FORMAT, sampleCount: this.samples,
      usage: GPUTextureUsage.RENDER_ATTACHMENT | GPUTextureUsage.TEXTURE_BINDING, label: 'depth',
    });
    this.bloomW = Math.max(1, width >> 2);
    this.bloomH = Math.max(1, height >> 2);
    const bloomDesc: GPUTextureDescriptor = {
      size: [this.bloomW, this.bloomH], format: HDR_FORMAT,
      usage: GPUTextureUsage.RENDER_ATTACHMENT | GPUTextureUsage.TEXTURE_BINDING,
    };
    this.bloomA = device.createTexture({ ...bloomDesc, label: 'bloomA' });
    this.bloomB = device.createTexture({ ...bloomDesc, label: 'bloomB' });
    this.volW = Math.max(1, width >> 1);
    this.volH = Math.max(1, height >> 1);
    this.volTex = device.createTexture({
      size: [this.volW, this.volH], format: HDR_FORMAT,
      usage: GPUTextureUsage.RENDER_ATTACHMENT | GPUTextureUsage.TEXTURE_BINDING, label: 'volumetric',
    });
    // Half resolution as well: a defocus disc has no detail to lose, and the
    // gather reads the full-resolution scene, so the taps stay sharp — which is
    // what keeps a bright speck a bright coin instead of a soft blob.
    const dofDesc = {
      size: [this.volW, this.volH] as [number, number], format: HDR_FORMAT,
      usage: GPUTextureUsage.RENDER_ATTACHMENT | GPUTextureUsage.TEXTURE_BINDING,
    };
    this.dofB = device.createTexture({ ...dofDesc, label: 'dof' });
    if (this.taa) {
      this.motion = device.createTexture({
        size: [width, height], format: MOTION_FORMAT, sampleCount: this.samples,
        usage: GPUTextureUsage.RENDER_ATTACHMENT | GPUTextureUsage.TEXTURE_BINDING, label: 'motion',
      });
      this.vMotion = this.motion.createView();
      // Two, ping-ponged: a pass cannot read the texture it is writing.
      this.history = [0, 1].map((k) => device.createTexture({
        size: [width, height], format: this.gpu.format,
        usage: GPUTextureUsage.RENDER_ATTACHMENT | GPUTextureUsage.TEXTURE_BINDING
             | GPUTextureUsage.COPY_SRC | GPUTextureUsage.COPY_DST, label: `history${k}`,
      }));
      this.vHistory = this.history.map((h) => h.createView());
      this.taaCold = true;
    }
    if (this.fxaa || this.taa) {
      this.ldr = device.createTexture({
        size: [width, height], format: this.gpu.format,
        usage: GPUTextureUsage.RENDER_ATTACHMENT | GPUTextureUsage.TEXTURE_BINDING
             | GPUTextureUsage.COPY_SRC, label: 'ldr',
      });
      this.vLdr = this.ldr.createView();
    }

    this.vHdr = this.hdr.createView();
    this.vHdrMS = msaa ? this.hdrMS.createView() : this.vHdr;
    this.vDepth = this.depth.createView();
    this.vMrf = this.mrf.createView();
    this.vMrfMS = msaa ? this.mrfMS.createView() : this.vMrf;
    this.vBloomA = this.bloomA.createView();
    this.vBloomB = this.bloomB.createView();
    this.vVol = this.volTex.createView();
    this.vDofB = this.dofB.createView();
    const hdrView = this.vHdr;
    const aView = this.vBloomA;
    const bView = this.vBloomB;

    this.brightBind = device.createBindGroup({
      layout: this.brightPipe.getBindGroupLayout(0),
      entries: [
        { binding: 0, resource: this.postSampler },
        { binding: 1, resource: hdrView },
      ],
    });
    const blurBind = (view: GPUTextureView) =>
      device.createBindGroup({
        layout: this.blurPipe.getBindGroupLayout(0),
        entries: [
          { binding: 0, resource: this.postSampler },
          { binding: 1, resource: view },
        ],
      });
    this.blurBinds = [blurBind(aView), blurBind(bView)];
    // Sampler, scene, DEPTH, uniform.
    //
    // Depth used to be deliberately absent here, on the reasoning that the
    // defocus rides in the scene's own alpha. That alpha is a per-LAYER
    // constant, so it can say "this is a backdrop" and cannot say "this is 40 m
    // past what you are looking at" — which left the play layer sharp at every
    // aperture. The gather needs both, so it needs the depth buffer.
    //
    // `layout: 'auto'` derives the group from what the entry point touches, so
    // adding a textureLoad in fsDof silently made this bind group one entry
    // short and every frame failed validation.
    this.dofBind = device.createBindGroup({
      layout: this.dofPipe.getBindGroupLayout(0),
      entries: [
        { binding: 0, resource: this.postSampler },
        { binding: 1, resource: hdrView },
        { binding: 3, resource: this.vDepth },
        { binding: 4, resource: { buffer: this.postBuf } },
      ],
    });

    // Only the three bindings the march actually reads. `layout: 'auto'` derives
    // the group from what the entry point touches, and a bind group carrying an
    // entry the layout does not declare is an error rather than a spare — which
    // is also why the pass can never read its own output: binding 6 is not here.
    this.causticBind = device.createBindGroup({
      layout: this.causticPipe.getBindGroupLayout(0),
      entries: [
        { binding: 4, resource: { buffer: this.postBuf } },
        // The splat walks its ray down onto the real seabed now, so it reads
        // the height map. The pass order already suits it: the map is drawn
        // before the scene, the splat after.
        { binding: 20, resource: this.vHeight },
      ],
    });

    this.volBind = device.createBindGroup({
      layout: this.volPipe.getBindGroupLayout(0),
      entries: [
        { binding: 3, resource: this.vDepth },
        { binding: 4, resource: { buffer: this.postBuf } },
        { binding: 5, resource: { buffer: this.coneBuf } },
        // Only the lights explicitly tagged as medium-scattering sources. The
        // surface pass still sees the complete list; this pass used to scan it
        // per pixel and reject every lamp/indicator before doing useful work.
        { binding: 15, resource: { buffer: this.scatterLightsBuf } },
        { binding: 16, resource: { buffer: this.scatterLightCountBuf } },
        // The caustic field and the height map, so a shaft in the column is the
        // same light as the patch on the seabed and a rock can interrupt it.
        { binding: 0, resource: this.postSampler },
        { binding: 19, resource: this.vCaustic },
        { binding: 20, resource: this.vHeight },
      ],
    });

    this.compositeBind = device.createBindGroup({
      layout: this.compositePipe.getBindGroupLayout(0),
      entries: [
        { binding: 0, resource: this.postSampler },
        { binding: 1, resource: hdrView },
        { binding: 2, resource: aView },
        { binding: 3, resource: this.vDepth },
        { binding: 4, resource: { buffer: this.postBuf } },
        // No binding 5: the cone-occlusion buffer moved to the volumetric pass
        // with the march that reads it, and `layout: 'auto'` drops what an entry
        // point does not touch — so passing it here is an error, not a spare.
        { binding: 6, resource: this.vVol },
        { binding: 7, resource: this.vDofB },
        { binding: 11, resource: this.fluid.current },
        { binding: 12, resource: this.vMrf },
        // The environment, so a reflection that leaves the screen has a
        // fallback instead of a hole. Same texture the scene pass samples.
        { binding: 13, resource: this.envSampler },
        { binding: 14, resource: this.envTex.createView({ dimension: '2d-array' }) },
        { binding: 19, resource: this.vCaustic },
        { binding: 20, resource: this.vHeight },
        // ONLY HERE. Every post pipeline uses `layout: 'auto'`, which derives
        // its layout from what its entry point actually READS — so a binding
        // handed to a group whose shader never mentions it is a validation
        // error, not spare capacity. `fsComposite` is the only one that
        // samples the studio; the caustic and volumetric groups must not
        // carry it.
        { binding: 21, resource: this.studioTex.createView() },
        // The panorama takes the slot when there is one: same equirect lookup,
        // different source, so nothing downstream has to know which it got.
        // Binding 15 is the panorama when there is one. WebGPU has no such
        // thing as an unbound texture, so when there is not, a 1x1 placeholder
        // stands in — `sky2.x` is zero and the shader never samples it.
      ],
    });

    if (this.taa) {
      // One per history parity. Only binding 9 differs; the rest is the frame.
      this.taaBinds = [0, 1].map((k) => device.createBindGroup({
        layout: this.taaPipe.getBindGroupLayout(0),
        entries: [
          { binding: 0, resource: this.postSampler },
          { binding: 3, resource: this.vDepth },
          { binding: 4, resource: { buffer: this.postBuf } },
          { binding: 8, resource: this.vLdr },
          { binding: 9, resource: this.vHistory[1 - k]! },
          { binding: 10, resource: this.vMotion },
        ],
      }));
      // Reading the buffer TAA writes, not the one it reads.
      this.sharpenBinds = [0, 1].map((k) => device.createBindGroup({
        layout: this.sharpenPipe.getBindGroupLayout(0),
        entries: [
          { binding: 0, resource: this.postSampler },
          { binding: 4, resource: { buffer: this.postBuf } },
          { binding: 8, resource: this.vHistory[k]! },
        ],
      }));
    }
    if (this.fxaa) {
      this.fxaaBind = device.createBindGroup({
        layout: this.fxaaPipe.getBindGroupLayout(0),
        entries: [
          { binding: 0, resource: this.postSampler },
          { binding: 4, resource: { buffer: this.postBuf } },
          { binding: 8, resource: this.vLdr },
        ],
      });
    }

    // Blur offsets, in UV units, widening each pass.
    const px = 1 / this.bloomW, py = 1 / this.bloomH;
    const dirs = [[px, 0], [0, py], [px * 2.4, 0], [0, py * 2.4]];
    for (let i = 0; i < this.blurBufs.length; i++) {
      const d = dirs[i % dirs.length];
      device.queue.writeBuffer(this.blurBufs[i], 0, new Float32Array([d[0], d[1], 0, 0]));
    }
  }

  /**
   * Step the sub-pixel offset for the next frame.
   *
   * Halton (2, 3) rather than random: it fills the pixel evenly at every prefix
   * length, so the accumulation converges smoothly instead of clumping in one
   * corner for a few frames and then over-correcting. Eight is long enough to
   * cover a pixel and short enough that a stationary camera settles quickly.
   *
   * Called once per frame from `advance`, before the camera builds its
   * matrices — the whole scheme depends on the projection and the resolve
   * agreeing about which offset this frame used.
   */
  advanceJitter() {
    if (!this.taa) { this.jitterX = 0; this.jitterY = 0; return; }
    const halton = (i: number, b: number) => {
      let f = 1, r = 0, n = i;
      while (n > 0) { f /= b; r += f * (n % b); n = Math.floor(n / b); }
      return r;
    };
    const k = (this.jitterIx = (this.jitterIx + 1) % 8) + 1;
    // Halton lands in [0, 1); centre it, then convert a pixel to NDC, where the
    // visible range is two units wide rather than one.
    this.jitterX = (halton(k, 2) - 0.5) * 2 / this.gpu.width;
    this.jitterY = (halton(k, 3) - 0.5) * 2 / this.gpu.height;
  }

  private writeGlobals(p: FrameParams, refract: boolean) {
    const g = this.globalsData;
    g.set(p.viewProj, 0);
    g[16] = p.camX; g[17] = p.camY; g[18] = p.camZ; g[19] = p.time;
    g[20] = p.sunX; g[21] = p.sunY; g[22] = p.sunZ; g[23] = p.sunStrength;
    // THE HAZE, SCALED. `fog.rgb` is the inscatter — the light the water itself
    // sends to the lens, with no albedo in it — and it is the term that decides
    // how much contrast a frame can have. Measured over the band table: it is
    // 28% of a pixel at 10 m, 57% at 30 m and 76% at 60 m, and those shares are
    // the SAME at every depth, because the surface term and the inscatter both
    // scale with the band's level. So this is not a depth problem and no
    // exposure curve reaches it; it is one number, and until now there was no
    // way to move it.
    //
    // `ambientLevel` is deliberately NOT scaled with it. That lights surfaces;
    // this fills the gaps between them. Pulling them together would just be a
    // brightness control, which the exposure curve already is.
    const hz = this.probe.haze ? Math.max(0, this.seaTune.haze) : 0;
    g[24] = p.fog[0] * hz; g[25] = p.fog[1] * hz; g[26] = p.fog[2] * hz;
    g[27] = p.ambientLevel;
    g[28] = p.ext[0]; g[29] = p.ext[1]; g[30] = p.ext[2]; g[31] = p.surfaceY;
    g[32] = p.ambient[0]; g[33] = p.ambient[1]; g[34] = p.ambient[2]; g[35] = p.normDepth;
    g[36] = p.lightX; g[37] = p.lightY; g[38] = p.lightZ; g[39] = p.lightRange;
    g[40] = p.lightDX; g[41] = p.lightDY; g[42] = p.lightDZ; g[43] = p.lightInner;
    g[44] = p.lightR; g[45] = p.lightG; g[46] = p.lightB; g[47] = p.lightOuter;
    g[48] = p.lightIntensity; g[49] = 0; g[50] = p.sonarRadius; g[51] = p.sonarStrength;
    g[52] = p.sonarX; g[53] = p.sonarY; g[54] = 0; g[55] = 0;
    g[56] = p.coneAngle; g[57] = p.coneHalf; g[58] = p.lightRange; g[59] = p.coneBins;
    // Motion vectors are measured between two UNJITTERED frames, so the pair
    // that goes to the shader is this frame's clean matrix and last frame's.
    g.set(p.viewProjNJ, 60);
    g.set(this.prevVP, 76);
    g[92] = p.dt;
    // Image-based lighting on, which the options screen can take away.
    g[93] = this.ibl ? 1 : 0;
    // zw: the render target's size in pixels, so a fragment can turn its own
    // `@builtin(position)` back into a uv. `fsJelly` needs that to sample the
    // backdrop it is refracting.
    g[94] = this.hdr.width; g[95] = this.hdr.height;
    // Foliage translucency lives in the SCENE pass, where the real surface
    // normal is and where the plants are actually shaded.
    g[96] = this.seaTune.leaf;
    // THE CAUSTIC WINDOW, COMPUTED ONCE HERE AND UPLOADED TWICE.
    //
    // It used to be derived inside post.wgsl. Three consumers read it now — the
    // splat that fills the map, the shafts and ground caustics that read it,
    // and the motes over in the particle pass, which is a different shader with
    // a different uniform. A window that two of them derive slightly
    // differently is light in the wrong place and nothing wrong in the code.
    const cLx = -p.sunX, cLy = -p.sunY;
    const cFocus = p.surfaceY - Math.min(60, Math.max(0.5, this.seaTune.causticSize));
    // THE LEAD IS A CONSTANT NOW, AND THAT IS THE WHOLE FIX FOR THE SWIMMING.
    //
    // It used to be the throw at the CAMERA'S OWN DEPTH, which put `camY` into
    // the window's origin — so rising and falling sailed the whole map
    // sideways over ground that was standing still, and the map's fade edge
    // swept across the frame with it. Panning looked fine because x and z are
    // snapped to texels; there was nothing to snap a depth term to.
    //
    // Taken at a fixed 60 m of reference depth instead. Now that the map is
    // 128 m either side rather than 64, one constant covers both consumers:
    //
    //     lead 35.8 m  ->  window [-92, +164] around the camera
    //     ground   screen +-58                          inside
    //     shafts   90 m  [ -9, +107]                    inside
    //              150 m [+27, +143]                    inside
    //              200 m [+57, +173]                    clipped at the far end
    //
    // So the window depends only on the sun and on x/z, and the sun only moves
    // when the hour does. Nothing about flying the boat can slide it any more.
    const LEAD_REF_DEPTH = 60;
    const cLead = Math.max(-HEIGHT_HALF_X, Math.min(HEIGHT_HALF_X,
      (cLx / Math.max(cLy, 0.25)) * LEAD_REF_DEPTH))
      * (this.probe.shaftLead ? this.seaTune.shaftLead : 0);

    // SNAPPED TO WHOLE TEXELS, WHICH IS WHY THE SHADOWS STOPPED SWIMMING.
    //
    // Both maps are rebuilt every frame over a window that follows the camera.
    // The CONTENT is world-space and does not move, but the texel grid it is
    // rasterised onto slid continuously with the boat — so the quantisation
    // edge of every shadow and every caustic filament crawled across the
    // ground as you flew, which reads exactly as "the shadows move when I
    // move". It is the oldest bug in shadow mapping and the oldest fix: round
    // the window's origin to a texel, so the grid lands on the same world
    // lattice every frame and the boat moves through a still pattern.
    //
    // The LEAD is snapped with it. It is part of the origin, and an unsnapped
    // term added to a snapped one is an unsnapped origin.
    const texX = (2 * HEIGHT_HALF_X) / CAUSTIC_MAP_X;
    const texZ = (2 * HEIGHT_HALF_Z) / CAUSTIC_MAP_Z;
    const snapX = Math.round((p.camX + cLead) / texX) * texX;
    // CENTRED ON THE FOCUS PLANE, NOT ON THE EYE.
    //
    // `camZ` is the lens, which sits 49.5 m back at zoom 1 and 67 m back
    // zoomed out — so the window ran z [camZ-192, camZ+64] and spent 64 m of
    // its 256 on empty water IN FRONT of the camera. The far parallax lane
    // lives at z -138 to -190; at zoom 1 that was 91 percent clipped out of the
    // map, and on the burner's 1.28 zoom it was gone entirely. A far-lane
    // overhang could never occlude anything, and the lane's front caps flicked
    // in and out of the map as the zoom changed.
    //
    // Off `focusZ` the window is [-192, +64] whatever the zoom does, which
    // holds every lane and stops depending on where the camera happens to be.
    const snapZ = Math.round(p.focusZ / texZ) * texZ;
    this.causticLead = snapX - Math.round(p.camX / texX) * texX;
    this.causticWin[0] = snapX - HEIGHT_HALF_X;
    this.causticWin[1] = snapZ - (HEIGHT_HALF_Z + HEIGHT_PUSH);
    this.causticWin[2] = 2 * HEIGHT_HALF_X;
    this.causticWin[3] = 2 * HEIGHT_HALF_Z;
    g.set(this.causticWin, 120);
    // The height map's projection, over THE SAME SNAPPED RECTANGLE — computed
    // just above for exactly this reason. The pass that rasterises the map and
    // every lookup that measures against it have to agree to the texel, or the
    // map answers about somewhere else.
    // ONE SLAB, computed here and read back by `heightAt`, which turns a depth
    // into a world y with it. Derived twice, they drift; derived once, they
    // cannot.
    // `min`, not `max`. The top is 90 m over the camera OR just above the
    // waterline, whichever is LOWER — so near the surface the slab reaches up
    // past a breaching hull, and at 600 m it is a 200 m band around the boat
    // instead of a 700 m one starting in the sky. `max` here pins the top at
    // +12 forever and the slab grows without bound as you descend, which is
    // the precision collapse this change exists to avoid.
    this.slabTop = Math.min(p.camY + HEIGHT_TOP, p.surfaceY + 12);
    this.slabBot = p.camY + HEIGHT_BOTTOM;
    orthoTopDown(this.heightVP,
      this.causticWin[0] - this.causticLead + HEIGHT_HALF_X,
      this.causticWin[1] + HEIGHT_HALF_Z, HEIGHT_HALF_X, HEIGHT_HALF_Z,
      // THE SLAB FOLLOWS THE PLAYER DOWN. It was world-absolute, +12 to -520,
      // and the authored seabed reaches -2566 — so past x ~4180 every texel in
      // the map was cleared and nothing in the back half of the game could
      // cast or receive a shadow. A 200 m slab around the camera covers
      // everything a frame can show with room over it for an occluder, and it
      // is 2.7x the depth precision of the old one into the bargain.
      //
      // Capped at the surface end so a breaching hull cannot push the top down
      // past the terrain it is supposed to be shadowed by.
      this.slabTop, this.slabBot);
    g.set(this.heightVP, 100);
    // 116..119 is `tuneA`. x is how much of the hero's studio rig to keep.
    g[116] = this.seaTune.studioRig;
    g[117] = this.seaTune.lampAo;
    g[118] = this.seaTune.pointWrap;
    // Strength doubles as the switch, and it wants the map the sun wanted: with
    // the height pass off there is nothing to read and the key goes back to
    // being unshadowed rather than reading a stale one. Chosen at 1 — the
    // blocker is taken for exactly what the map says it is, no fudge.
    const cN = Math.hypot(p.sunX, p.sunY, p.sunZ) || 1;
    g[124] = -p.sunX / cN; g[125] = -p.sunY / cN; g[126] = -p.sunZ / cN;
    g[127] = cFocus;
    // Strength of the shaft term on a mote. Same box, same slider.
    g[128] = this.probe.moteSun ? this.seaTune.moteSun : 0;
    // How much the AMBIENT DOME believes the height map. See `skyAbove` in
    // scene.wgsl: the dome has never been occluded from above on terrain,
    // because the `ao` that was standing in for it measures slab thickness.
    // On the height map too, like every other consumer of it — without that,
    // unticking the map left the ambient dome reading whatever was last
    // rasterised into a texture nobody was updating.
    g[129] = this.probe.skyAbove && this.probe.heightMap ? this.seaTune.skyAbove : 0;
    // DEPTH PER METRE in the height map's projection. The slab moves with the
    // camera now, so this is not a constant any more — and every bias in every
    // consumer of that map was a raw depth number tuned against the old fixed
    // 532 m slab. Uploading the scale lets them all be written in METRES,
    // which is what they were always trying to say.
    g[130] = 1 / Math.max(1e-3, this.slabTop - this.slabBot);
    // The studio's gain for the SCENE pass. Same slider as the composite's, so
    // one control moves both halves of the same reflection.
    g[131] = this.probe.studio ? this.seaTune.studio : 0;
    // 132..135 is `tuneD`. x is how hard the reference art modulates terrain —
    // see the note in `fsTerrain`: those layers are renders of objects, and at
    // full strength they tile as a wallpaper of coral rather than as grain.
    g[132] = this.probe.artDetail ? this.seaTune.artDetail : 0;
    // y is the refraction strength — and it is ZERO whenever the pass was not
    // split, because the backdrop then holds last frame's scene and bending to
    // it would smear. The shader must never read a backdrop that is not this
    // frame's.
    g[133] = refract ? this.seaTune.refraction : 0;
    // z is the particle KIND MASK — one bit per KIND in particles.ts, culled in
    // the vertex stage so an unticked kind costs a clipped triangle and no
    // fragment. See `pKind`.
    const K = this.probe.pKind;
    g[134] = (K.ambient ? 1 : 0) | (K.bubble ? 2 : 0) | (K.spark ? 4 : 0)
      | (K.debris ? 8 : 0) | (K.cloud ? 16 : 0) | (K.plankton ? 32 : 0)
      | (K.spray ? 64 : 0);
    // w — the z falloff, scaling `fogDist`. Read by scene.wgsl and by the
    // particles, which carry their own copy of the same distance.
    g[135] = Math.max(0, this.seaTune.zFall);
    // tuneE.x — how much of the water's own ambient a specular lobe gets back.
    // See `ambientSpec`: without it a conductor in open water is lit by the
    // searchlight and nothing else.
    g[136] = this.probe.metalAmbient ? Math.max(0, this.seaTune.metalAmbient) : 0;
    // y — the ore's achromatic self-colour. See `crystalCoat`: the honest
    // ambient cannot make copper gold in blue water, because there is no red
    // in the water to reflect.
    g[137] = this.probe.oreSheen ? Math.max(0, this.seaTune.oreSheen) : 0;
    // z — the crystal coat itself, so the whole ore look can be A/B'd.
    g[138] = this.probe.crystal ? 1 : 0;
    // w — translucency, light arriving through the far side of a stone.
    g[139] = this.probe.crystal ? Math.max(0, this.seaTune.oreTranslucency) : 0;
    // tuneF.x — how much diffuse a gem keeps. The checkbox is the LOOK: ticked
    // is glass, unticked is an ordinary solid shaded like everything else.
    g[140] = this.probe.gemGlass ? Math.max(0, this.seaTune.gemDiffuse) : 1;
    // How much of an object's OWN light survives the refit panel's stand damp.
    // See the `onStand` branch in scene.wgsl: 0 is the damped 0.30 this always
    // was, 1 returns the emission whole.
    g[141] = this.probe.standEmissive ? Math.max(0, this.seaTune.standEmissive) : 0;
    // tuneF.z — glass transmission. 0 makes `fsGlass` return a fully covered
    // pane, which is the old opaque look, so the checkbox is a real A/B rather
    // than a fade to nothing.
    g[142] = this.probe.glassTransmit ? 1 : 0;
    // tuneF.w — triplanar detail. Off returns the mesh path to the single world
    // XY projection it had, which is the honest A/B: the difference is entirely
    // in how the SAME maps are projected and how their normals are oriented.
    g[143] = this.probe.triplanar ? 1 : 0;
    // 144..147 is `tuneG`. x is how hard the detail set drives roughness — the
    // half of a texture set this renderer never had. See the block above
    // `roughBreak` in scene.wgsl.
    g[144] = this.probe.surfaceWear ? Math.max(0, this.seaTune.surfaceWear) : 0;
    // y is the glass body floor — see `fsGlass`. Zero is pure Fresnel, which
    // makes a clean pane invisible face-on.
    g[145] = this.probe.glassTransmit ? Math.max(0, this.seaTune.glassBody) : 0;
    // z is the glass refraction dial. Multiplies `tuneD.y`, so it rides the
    // same backdrop-is-valid gate the jellyfish do — bending to a backdrop that
    // is not this frame's would smear.
    g[146] = this.probe.glassTransmit ? Math.max(0, this.seaTune.glassRefract) : 0;
    // w — the split-sum DFG term in place of `fresnelRough`. A switch rather
    // than a replacement because this one changes BRIGHTNESS, and a brightness
    // change with nothing to compare against is indistinguishable from a tuning
    // mistake. Mirrored into the post pass at q[164].
    g[147] = this.probe.dfg ? 1 : 0;
    // 148..151 is `tuneH`. x is geometric specular antialiasing — see the block
    // above `mRoughV` in scene.wgsl. It exists because the frame is rendered
    // below display resolution by the tier's pixel budget, and a mirror is the
    // first thing to alias when it is.
    g[148] = this.probe.specAA ? Math.max(0, this.seaTune.specAA) : 0;
    // y — the boat's viewports shaded as glass rather than as lit plates. See
    // `subGlassW` in scene.wgsl for why this is a diffuse cut and not the
    // translucent pass.
    g[149] = this.probe.subGlass ? Math.max(0, this.seaTune.subGlass) : 0;
    // z — the boat's own unwrap scale, which sets its plate size.
    g[150] = Math.max(0.05, this.seaTune.subPanel);
    // w — the boat's paint on the panel-seam layer (1) or the old plating scan (0).
    g[151] = this.probe.subPlate ? 1 : 0;
    // 152..155 is `tuneI`. x is how finely the detail set tiles on BUILT
    // surfaces — see `dScale` in scene.wgsl. The seabed keeps 0.42.
    g[152] = Math.max(0.05, this.seaTune.builtDetail);
    // y — how much of the detail set a BUILT surface takes. The seabed keeps
    // the full amount; steel and paint want a fraction of it. See `builtGrain`.
    g[153] = this.probe.builtGrain ? Math.max(0, this.seaTune.builtGrain) : 0;
    // z — how much of the sea's ambient a habitat cabin keeps. Near zero is
    // "there is air in here"; 1 puts it back underwater. See `wCabin`.
    g[154] = this.probe.cabinAir ? Math.max(0, this.seaTune.cabinAmbient) : 1;
    // w — refraction on tissue (jellies, foam) as opposed to glass. The glass
    // path reads `tuneD.y` directly; this scales only `refractDelta`.
    g[155] = this.probe.jellyRefraction ? 1 : 0;
    /**
     * 156..159 is `tuneJ`: the lamp's intensity scale and its two falloff
     * terms. OFF reproduces what shipped exactly — 1.0, 0.055, 0.0030 — so the
     * checkbox is a true A/B rather than a fade.
     *
     * The shipped numbers light a distant seabed through absorbing water and
     * are far too hot up close: at `searchIntensity` 12 a 0.78-albedo hull
     * three metres from the lamp receives about 12.8, and the filmic tonemap
     * saturates near 4. Everything within twenty metres of the beam is
     * therefore full white, habitat or seabed — the seabed simply hides it
     * better by being darker.
     */
    // ALWAYS the sliders. Gating them behind the checkbox made dragging one
    // with the box unticked do nothing, which is indistinguishable from broken.
    // The checkbox is a preset that WRITES these; their defaults are the
    // shipped constants, so an untouched build is bit-identical to before.
    g[156] = Math.max(0, this.seaTune.lampScale);
    g[157] = Math.max(0, this.seaTune.lampLinear);
    g[158] = Math.max(0, this.seaTune.lampQuadratic);
    g[159] = 0;
    g[119] = this.probe.rigShadow && this.probe.heightMap
      ? this.seaTune.rigShadow : 0;
    g[97] = this.seaTune.bioGlow;
    // The same number the composite's fallback uses, so one control governs the
    // whole environment term instead of the quarter of it that could be reached.
    g[98] = this.seaTune.envGain;
    // Terrain and mesh self-shadowing from the height map, as a strength rather
    // than a flag, so the switch and the taste are the same control.
    g[99] = this.probe.sunShadow && this.probe.heightMap ? 1 : 0;

    this.prevVP.set(p.viewProjNJ);
    this.device.queue.writeBuffer(this.globalsBuf, 0, g);

    // The probe zeroes the COUNT rather than the placement, so the only thing
    // removed is the per-fragment loop the lights cost -- everything upstream
    // that decides where they go still runs and still shows in the CPU figure.
    this.lightCountData[0] = this.probe.lights ? this.lightCount : 0;
    this.device.queue.writeBuffer(this.lightCountBuf, 0, this.lightCountData);
    if (this.lightCount > 0) {
      this.device.queue.writeBuffer(this.lightsBuf, 0, this.lightsData, 0, this.lightCount * 8);
    }
    this.scatterLightCountData[0] = this.probe.lights ? this.scatterLightCount : 0;
    this.device.queue.writeBuffer(this.scatterLightCountBuf, 0, this.scatterLightCountData);
    if (this.scatterLightCount > 0) {
      this.device.queue.writeBuffer(
        this.scatterLightsBuf, 0, this.scatterLightsData, 0, this.scatterLightCount * 8,
      );
    }

    const q = this.postData;
    q.set(p.invViewProj, 0);
    q[16] = p.camX; q[17] = p.camY; q[18] = p.camZ; q[19] = p.time;
    q[20] = p.ext[0]; q[21] = p.ext[1]; q[22] = p.ext[2]; q[23] = p.surfaceY;
    // The same scale the scene pass got — see `writeGlobals`. The composite
    // marches its own inscatter and the two must agree, or the volumetric and
    // the surfaces would be sitting in different amounts of water.
    const hzp = this.probe.haze ? Math.max(0, this.seaTune.haze) : 0;
    q[24] = p.fog[0] * hzp; q[25] = p.fog[1] * hzp; q[26] = p.fog[2] * hzp;
    q[27] = p.ambientLevel;
    q[28] = p.lightX; q[29] = p.lightY; q[30] = p.lightZ; q[31] = p.lightRange;
    q[32] = p.lightDX; q[33] = p.lightDY; q[34] = p.lightDZ; q[35] = p.lightInner;
    q[36] = p.lightR; q[37] = p.lightG; q[38] = p.lightB; q[39] = p.lightOuter;
    // Exposure, with the player's own multiplier on it. A brightness control
    // belongs here rather than on the final colour: multiplying at the end
    // lifts the black point with everything else and turns the water grey,
    // where scaling the exposure feeds the tonemap more light and lets its
    // shoulder do what it is for.
    q[40] = p.lightIntensity; q[41] = p.exposure * this.brightness;
    q[42] = p.vignette; q[43] = p.distortion;
    q[44] = this.gpu.width; q[45] = this.gpu.height;
    q[46] = 1 / this.gpu.width; q[47] = 1 / this.gpu.height;
    q[48] = p.sonarX; q[49] = p.sonarY; q[50] = 0; q[51] = p.sonarRadius;
    q[52] = p.sonarStrength; q[53] = p.damageFlash; q[54] = p.sunStrength; q[55] = p.bloom;
    q[56] = p.coneAngle; q[57] = p.coneHalf; q[58] = p.lightRange; q[59] = p.coneBins;
    q[60] = this.probe.volumetric;
    // How far the boat is from the eye, so the wake can refract only what is
    // BEHIND it — the hull sits in front of the water it stirs and has no
    // business being bent by it.
    q[61] = Math.hypot(p.wakeX - p.camX, p.wakeY - p.camY, p.camZ - p.wakeZ);
    q[62] = this.probe.wake ? 1 : 0;
    q[63] = 0;
    // f/2.8 is the reference: it reproduces the single fixed look this used to
    // have, so the old picture is still on the dial rather than replaced by it.
    // The boat on screen, and how big a hole to leave around it. The wake has
    // to START somewhere aft of the hull — foam that begins at the plating
    // looks stuck to it rather than shed by it — so the composite gates the
    // whole contribution out inside this radius regardless of where the
    // simulation put it.
    q[68] = p.wakeSubU; q[69] = p.wakeSubV; q[70] = p.wakeGap; q[71] = p.wakeNitro;
    // DEPTH OF FIELD, WHICH IS A LANE SEPARATOR AND NOT A LENS.
    //
    // It has been three things now. A per-layer constant, which had no focal
    // plane at all and so no aperture could touch the play lane. Then a true
    // distance solve, which had one and put it in the wrong place: the play
    // plane is a PLANE, and distance-from-camera across a plane grows toward
    // the edges of the frame, so the boat came out sharp and the water either
    // side of it did not.
    //
    // The lanes ARE the depth in this game. The player is in one of them and
    // cannot touch the others, so what the blur has to say is which water you
    // are in — and `LAYER.params.z` has carried exactly that all along: 0 for
    // the lane you fly, 0.55 for the next, 1 for the far one. The aperture
    // scales it and nothing else does.
    //
    //   x  maximum blur radius, as a fraction of frame height
    //   y  aperture, 0 to 1, multiplying the lane defocus
    //   z  distance to the play plane — several other effects fade against it
    //   w  on/off
    //
    // 5.5 percent of the frame's HEIGHT, not 60 pixels. A radius in pixels is a
    // different aperture on every monitor — the same shot at 4K was half as
    // soft as at 1080p, which is not something a lens does.
    q[64] = this.dof > 0 ? this.gpu.height * 0.055 : 0;
    // THE APERTURE, AS A PLAIN 0 TO 1 ON THE LANE DEFOCUS.
    //
    // Depth of field here is not a distance solve any more. The lanes are the
    // depth in this game — the player is in one of them and cannot touch the
    // others — so the honest thing for the blur to say is which water you are
    // in, and the layer defocus already carried exactly that: 0, 0.55, 1. This
    // scales it. f/1.2 is the full authored separation, f/8 is a hint of it.
    //
    // Zoom no longer enters into it. A longer lens really does throw the
    // background further out, but the thing being separated here is a lane, and
    // a lane does not become a different lane when the camera pushes in.
    q[65] = this.dof > 0 ? Math.min(1, 1.2 / this.dof) : 0;
    q[66] = p.focusDist;
    q.set(p.viewProj, 72);
    // Appended at the END of the struct rather than squeezed into a spare
    // component: `wake` and `dof` already share the neighbourhood and a slot
    // written twice is a bug that shows up as one effect silently disabling
    // another. 88..91, and postData grew to match.
    q[88] = this.ssfx ? 1 : 0;
    // TAA on, which is what licenses the screen-space effects to jitter over
    // time. Without an accumulator behind them that jitter is just boiling.
    q[89] = this.taa ? 1 : 0;
    // Debug: paint the circle of confusion instead of the scene.
    q[90] = this.probe.cocView ? 1 : 0;
    // Sharpening, and only the TAA path has somewhere to apply it.
    q[91] = this.taa ? this.sharpness : 0;
    // The same four numbers the shader is about to use, in plain JS. Reading
    // them off `ABYSS.renderer.dofInfo` settles in one line what a screenshot
    // of a grey rectangle cannot: whether the aperture reached the shader at
    // all, and what it was worth by the time it did.
    this.dofInfo.stop = this.dof;
    this.dofInfo.maxRadiusPx = q[64];
    this.dofInfo.scale = q[65];
    this.dofInfo.focusDist = q[66];
    this.dofInfo.viewZoom = p.viewZoom;
    // What each lane is worth at this aperture: play plane, mid, far.
    this.dofInfo.perLane = [0, 0.55 * q[65], 1 * q[65]];
    q[67] = this.dof > 0 ? 1 : 0;
    // The sun, so the composite can shadow it. 92..95.
    q[92] = p.sunX; q[93] = p.sunY; q[94] = p.sunZ; q[95] = p.sunStrength;
    // Per-effect sample counts, 96..99. The master switch multiplies them all
    // to zero rather than being a second thing the shader has to test.
    const on = this.ssfx ? 1 : 0;
    q[96] = AO_TAPS[this.gfxAo]! * on;
    q[97] = SHADOW_STEPS[this.gfxShadow]! * on;
    q[98] = SSR_STEPS[this.gfxSsr]! * on;
    q[99] = this.probe.taaDepthClamp ? 1 : 0;
    // Sky: how much of it is visible, how many cloud octaves to spend, and
    // where the waterline is. 100..103.
    q[100] = p.skyAmount * (this.gfxSky > 0 ? 1 : 0);
    q[101] = SKY_OCTAVES[this.gfxSky]!;
    q[102] = p.surfaceY;
    q[103] = p.planeZ;
    q[104] = p.sunR; q[105] = p.sunG; q[106] = p.sunB;
    q[107] = this.gfxSky > 0 ? this.cloudCover : 0;
    q[108] = this.seaSurface ? p.planeView : 0;
    q[109] = p.night;
    // Bit 0 selects the baked underwater IBL A/B; bit 1 selects the folded wave
    // profile; bit 2 enables screen-space scene bending under the surface.
    // Keeping these in one bitfield leaves the Post uniform layout unchanged.
    q[110] = (this.probe.surfaceSky ? 1 : 0)
      + (SEA.legacyProfile ? 2 : 0)
      + (this.probe.surfaceRefraction ? 4 : 0);
    q[111] = this.probe.surfaceMicro ? 1 : 0;
    const T = this.skyTune;
    q[112] = T.density;   q[113] = T.scaleHeight; q[114] = T.rayleigh; q[115] = T.mie;
    q[116] = T.mieMul;    q[117] = T.mieG;        q[118] = T.odCap;    q[119] = T.sunIntensity;
    q[120] = T.polarise;  q[121] = T.cloudAlt;    q[122] = T.mistNear; q[123] = T.mistFar;
    const S = this.seaTune;
    q[124] = S.foam;      q[125] = S.frothScale;  q[126] = S.glint;    q[127] = S.reflection;
    // INVERTED HERE so the slider can mean what it says. The shader multiplies
    // world position by this to get its noise coordinate, so a SMALLER number
    // is a BIGGER pattern — which made the control read backwards. The panel
    // now asks for a size in metres and this turns it into a frequency.
    // Metres, straight through. It was `1 / size` for the noise pattern's
    // frequency; it is now the depth the splat projects to.
    q[128] = S.caustic;   q[129] = S.causticSize;
    q[130] = S.causticReach; q[131] = S.envGain;
    // The sea's shape comes from SEA itself, which the physics reads: one copy,
    // so a slider cannot move the picture without moving the hull with it.
    q[132] = SEA.k;       q[133] = SEA.amp;       q[134] = SEA.speed;  q[135] = SEA.chop;
    q[136] = S.normalEps; q[137] = S.normalFlat;  q[138] = S.detailOct; q[139] = S.tintDepth;
    q[140] = SEA.fall;    q[141] = SEA.mul;       q[142] = S.waveRelax; q[143] = SEA.octaves;
    q[144] = S.bioScatter;
    q[145] = p.skyX; q[146] = p.skyY; q[147] = p.skyZ;
    // The height map's slab, so a depth can be turned back into a world y
    // without a second copy of the matrix that made it.
    // The SAME slab `heightVP` was built with — see `slabTop`. These decode a
    // depth back to a world y, so a second derivation here is a second chance
    // to disagree with the projection that wrote the map.
    q[148] = this.slabTop;
    q[149] = this.slabBot;
    q[150] = this.probe.heightMap ? 1 : 0;
    q[151] = this.probe.phase ? 1 : 0;
    // 152..155 is `tuneA`. Both shaft refinements, both zero when their box is
    // unticked — the beam they refine already works and neither gets to be the
    // reason it stopped.
    // 156..159 is `tuneB`: the caustic window, computed once in `globals`.
    q.set(this.causticWin, 156);
    // 160..163 is `tuneC`. x is the seabed's bounce onto undersides — its own
    // control now, because it used to ride on the caustic slider and that made
    // one number quietly govern two unrelated things.
    q[160] = this.probe.caustics ? this.seaTune.bounce : 0;
    // tuneC.y — the DOF bleed guard, read by `fsDof`.
    q[161] = this.probe.dofGuard ? 1 : 0;
    // tuneC.z — how hard the atmosphere reddens the sun as it drops. Neutral at
    // zenith by construction, so 0 and "sun overhead" are the same picture.
    // STRENGTH TIMES THE EXTRA AIR, relative to the preset's own elevation.
    // See `sunTint`: normalising against the zenith instead double-counted the
    // preset's authored sun colour and turned the sky olive. Clamped at zero,
    // so raising the sun above its preset gives the preset back rather than a
    // bluer sun than anyone asked for.
    const odAt = (sinE: number) => T.scaleHeight / Math.max(sinE + 0.045, 0.045);
    const extraAir = Math.max(0, odAt(-p.skyY) - odAt(p.sunRefY));
    q[162] = this.probe.sunset ? Math.max(0, this.skyTune.sunset) * extraAir : 0;
    // tuneC.w — 0 tints the whole sky, 1 confines the tint to the horizon.
    q[163] = Math.max(0, Math.min(1, this.skyTune.sunsetGradient));
    q[152] = this.probe.shaftCore ? this.seaTune.shaftCore : 0;
    q[153] = this.probe.shaftGrain ? this.seaTune.shaftGrain : 0;
    q[154] = this.probe.studio ? this.seaTune.studio : 0;
    // ONE WRITE. There were two, and the second was a stale `= 0` left over
    // from when this slot was padding — so the lead never reached the shader
    // and the entire fix that introduced it was inert from the moment it was
    // written. `heightBase()` collapsed to `causticBase()` and the height map
    // went on being read at the caustic window's origin.
    q[155] = this.causticLead;
    // 164..167 is `tuneD`. x mirrors `probe.dfg` so the composite's screen-space
    // reflections use the same reflectance model as the forward pass — see the
    // struct comment in post.wgsl for why they must agree.
    q[164] = this.probe.dfg ? 1 : 0;
    // y — the one-bounce colour bleed. Its source is the blurred bloom buffer
    // now rather than point samples of the sharp scene; see `gi` in post.wgsl.
    q[165] = this.probe.ssgi ? Math.max(0, this.seaTune.ssgi) : 0;
    q[166] = 0; q[167] = 0;
    this.device.queue.writeBuffer(this.postBuf, 0, q);

  }

  render(p: FrameParams, chunks: Chunk[], drawSurface: boolean, sim: {
    halfW: number; halfH: number; dt: number; flowX: number; flowY: number;
    px: number; py: number; speed: number;
  }) {
    const device = this.device;
    const wantsRefraction = this.probe.instances
      && this.probe.refraction && this.seaTune.refraction > 0.001;
    const regions = wantsRefraction ? this.backdropRegions(p) : [];
    const refract = regions.length > 0;
    const msaa = this.samples > 1;
    this.stats.backdropEnabled = wantsRefraction;
    this.stats.backdropRegions = refract ? (msaa ? 1 : regions.length) : 0;
    this.stats.backdropPixels = refract
      ? (msaa ? this.hdr.width * this.hdr.height
        : regions.reduce((sum, r) => sum + rectArea(r), 0))
      : 0;
    this.stats.backdropCoverage = this.stats.backdropPixels
      / Math.max(1, this.hdr.width * this.hdr.height);
    this.writeGlobals(p, refract);
    this.writeLayers(p);

    if (this.probe.instanceUpload) {
      for (const slot of this.slots.values()) {
        if (slot.count) {
          device.queue.writeBuffer(slot.buffer, 0, slot.data, 0, slot.count * INSTANCE_FLOATS);
        }
      }
    }

    // Bracket the whole command buffer. Per-pass timing would be finer, but
    // the question being asked is "is the GPU the reason the frame is late",
    // and that is one number.
    // Bracketed by two EMPTY compute passes. `writeTimestamp` was dropped from
    // the spec in favour of `timestampWrites` on a pass descriptor, and a pass
    // that dispatches nothing costs nothing — which beats threading a flag
    // through the three different passes that can end a frame.
    const ts = this.tsSet;
    // Per frame, because the pass list is per frame. See `TS_QUERIES`.
    // Slot 0 is the frame's opening mark; it carries the empty label because it
    // measures nothing itself, it only starts the clock.
    this.tsUsed = 1;
    this.tsNames = [''];
    const enc = device.createCommandEncoder({ label: 'frame' });
    if (ts) {
      this.tsMark(enc, 'ts-open', { querySet: ts, endOfPassWriteIndex: 0 });
    }

    // The wake, before anything reads it. It is a world-space field, so it is
    // handed the camera window rather than the screen.
    if (this.probe.wake) {
      // Bracketed rather than instrumented from inside: the solver owns its own
      // passes and has no business knowing this profiler exists. See `tsRegion`.
      const done = this.tsRegion(enc, 'wake');
      this.fluid.step(enc, {
        x: p.wakeX, y: p.wakeY, vx: p.wakeVX, vy: p.wakeVY, thrust: p.wakeThrust,
        dt: sim.dt, camX: p.camX, camY: p.camY, halfW: sim.halfW, halfH: sim.halfH,
      });
      done();
    }

    // THE MOTE BOX DOES NOT BREATHE WITH THE ZOOM.
    //
    // Ambient particles are a fixed COUNT in a box that used to be sized from
    // the current view, so zooming out spread the same motes over more water:
    // measured, nitro's 1.28 zoom drops the density to 74% and full user zoom
    // to 55%. That is the water visibly emptying the moment the burner lights,
    // reported as losing the ambience, and it is arithmetic rather than a
    // driver or a limit — which is why it never showed on a desktop, where
    // there are three times as many motes to lose a quarter of.
    //
    // Pinned to the widest zoom the game asks for, so the density is constant
    // and the box still covers the frame at full burn.
    const PIN = 1.36;
    const boxH = Math.max(sim.halfH, (CAMERA_VIEW_HEIGHT * 0.5) * PIN);
    const boxW = Math.max(sim.halfW, boxH * (this.gpu.width / this.gpu.height));
    const doneSim = this.tsRegion(enc, 'particles:sim');
    this.particles.simulate(
      enc, p.camX, p.camY,
      boxW + 26, boxH + 26, 78,
      sim.dt, p.time, sim.flowX, sim.flowY, sim.px, sim.py, sim.speed,
      sim.halfW, sim.halfH,
    );
    doneSim();

    // --- scene ------------------------------------------------------------
    // THE HEIGHT MAP, drawn before the scene because everything downstream is a
    // consumer of it and nothing it draws depends on the frame.
    if (this.probe.heightMap) {
      const hp = enc.beginRenderPass({
        label: 'height',
        timestampWrites: this.tsPass('height'),
        colorAttachments: [],
        depthStencilAttachment: {
          view: this.vHeight,
          depthClearValue: 1,
          depthLoadOp: 'clear',
          depthStoreOp: 'store',
        },
      });
      hp.setBindGroup(0, this.heightBind);
      if (this.probe.terrain) {
        hp.setPipeline(this.heightTerrainPipe);
        const lc = Math.max(1, Math.min(LAYERS.length,
          Math.round(Number(this.probe.farLayers))));
        for (let li = 0; li < lc; li++) {
          // No group 1: the depth-only vertex shader has no use for a layer's
          // tint, and a slot bound but unread is a slot that can go stale.
          for (const c of chunks) {
            const m = c.meshes[li];
            if (!m || !m.indexCount) continue;
            hp.setVertexBuffer(0, m.vbo);
            hp.setIndexBuffer(m.ibo, 'uint32');
            hp.drawIndexed(m.indexCount);
          }
        }
      }
      if (this.probe.instances) {
        hp.setPipeline(this.heightMeshPipe);
        for (const slot of this.slots.values()) {
          // Translucent things do not cast: a jellyfish is not a roof, and a
          // curtain of kelp would shadow the whole seabed it grows out of.
          if (!slot.count || slot.translucent) continue;
          hp.setBindGroup(1, slot.bind);
          hp.setVertexBuffer(0, slot.mesh.vbo);
          hp.setIndexBuffer(slot.mesh.ibo, 'uint32');
          hp.drawIndexed(slot.mesh.indexCount, slot.count);
        }
      }
      hp.end();
    }

    /**
     * SPLIT ONLY WHEN REFRACTION IS ON.
     *
     * Refraction needs the opaque scene resolved before the translucent draws
     * can sample it, which means two passes, and two passes means the MSAA
     * target has to be STORED between them instead of discarded. That is real
     * bandwidth on a tile GPU. When the box is unticked the frame is exactly
     * the single pass it always was, so the control removes the cost and not
     * just the effect.
     */
    const scene = enc.beginRenderPass({
      label: refract ? 'scene:opaque' : 'scene',
      timestampWrites: this.tsPass(refract ? 'scene:opaque' : 'scene'),
      colorAttachments: [{
        view: this.vHdrMS,
        // When split, the first half resolves to the BACKDROP — with MSAA the
        // resolve it already owed is the copy, so the split is nearly free.
        ...(msaa ? { resolveTarget: refract ? this.vBackdrop : this.vHdr } : {}),
        clearValue: {
          r: p.fog[0] * p.ambientLevel,
          g: p.fog[1] * p.ambientLevel,
          b: p.fog[2] * p.ambientLevel,
          a: 0,
        },
        loadOp: 'clear',
        // Kept, not discarded, when a second half is going to load it.
        storeOp: (msaa && !refract ? 'discard' : 'store') as GPUStoreOp,
      }, ...(this.taa ? [{
        view: this.vMotion,
        // Zero means "did not move", which is the right answer for the sky and
        // for anything the scene pass never covered.
        clearValue: { r: 0, g: 0, b: 0, a: 0 },
        loadOp: 'clear' as const,
        storeOp: 'store' as const,
      }] : []), {
        view: this.vMrfMS,
        ...(this.samples > 1 ? { resolveTarget: this.vMrf } : {}),
        // All zero: no metal, no roughness, no reflectivity. Whatever the scene
        // pass never covered is open water, and water is not a surface.
        clearValue: { r: 0, g: 0, b: 0, a: 0 },
        loadOp: 'clear' as const,
        storeOp: (msaa && !refract ? 'discard' : 'store') as GPUStoreOp,
      }],
      depthStencilAttachment: {
        view: this.vDepth,
        depthClearValue: 1,
        depthLoadOp: 'clear',
        depthStoreOp: 'store',
      },
    });

    // Near layer first. The terrain is opaque and writes depth, so layer order
    // cannot change the image — but `fsTerrain` is by a distance the heaviest
    // shader in the frame (three fbm evaluations for the relief normal alone),
    // and drawing back-to-front paid for it two or three times over on every
    // pixel where the near ground covers the background. Front-to-back lets
    // early-Z throw those fragments away before they are shaded. In open water
    // it changes little; in a canyon, where the near layer covers the screen,
    // it is most of the terrain's fill cost.
    scene.setPipeline(this.terrainPipe);
    scene.setBindGroup(0, this.globalsBind);
    // Zero when the seabed is switched off, which the clamp below would not
    // otherwise allow — `farLayers` is a count of PARALLAX slabs and one is its
    // floor, but "none at all" is a different question and a measurable one.
    const layerCount = this.probe.terrain
      ? Math.max(1, Math.min(LAYERS.length, Math.round(Number(this.probe.farLayers))))
      : 0;
    for (let li = 0; li < layerCount; li++) {
      scene.setBindGroup(1, this.terrainBinds[li]);
      // How much world height this layer can show. Measured at its back face,
      // which is the farthest from the camera and so the most permissive.
      // Measured at the layer's back face, which is farthest from the camera
      // and so the most permissive. `camY`/`camZ` are the EYE, which is why the
      // span is taken from it rather than from the point being looked at.
      // Distance to the layer's FAR face, whichever side the camera is on. A
      // camera swung round behind the stack has a negative `camZ - zBack`, and
      // a negative reach inverts the band and culls the layer outright.
      const L = LAYERS[li]!;
      // Nothing between the eye and what it is looking at.
      if (layerVisibility(p.camZ, L.zFront) <= 0) continue;
      const reach = Math.max(Math.abs(p.camZ - L.zFront), Math.abs(p.camZ - L.zBack));
      const yHi = p.camY - reach * p.tanTop + 8;
      const yLo = p.camY - reach * p.tanBot - 8;
      for (const c of chunks) {
        const m = c.meshes[li];
        if (!m) continue;
        let first = 0;
        let count = m.indexCount;
        const sy = m.sliceY;
        const ss = m.sliceStart;
        if (sy && ss) {
          const n = sy.length - 1;
          if (sy[0] >= yHi || sy[n] <= yLo) continue;
          let a = 0;
          while (a < n && sy[a + 1] <= yLo) a++;
          let b = n - 1;
          while (b > a && sy[b] >= yHi) b--;
          first = ss[a];
          count = ss[b + 1] - first;
        }
        if (count <= 0) continue;
        this.stats.terrainTris += count / 3;
        this.stats.terrainDraws++;
        scene.setVertexBuffer(0, m.vbo);
        scene.setIndexBuffer(m.ibo, 'uint32');
        scene.drawIndexed(count, 1, first);
      }
    }

    // THE SEA IS NOT DRAWN HERE ANY MORE, and there is a good story in why.
    //
    // This drew an 1800 m grid at `surfaceY` with its own four-sine wave field,
    // and it had been doing so since long before the composite learned to draw
    // water. When the composite grew a surface of its own the two coexisted —
    // different waves, different phase, different amplitude, both at the
    // waterline — and the game had, quite literally, two seas. That is what a
    // player sees as "two water planes", and no amount of tuning either one
    // could have resolved it, because they were both working.
    //
    // The composite's is the one that survives: five-octave detail normals,
    // refraction, absorption, caustics and cellular froth, all of which need
    // the finished scene colour to sample and none of which a scene-pass mesh
    // can have, because it is writing into the very target it would need to
    // read. The mesh's only real advantage was depth sorting, and the composite
    // replaced that with a height test that suits this game's parallax slabs
    // better than a depth compare ever did.
    //
    // `drawSurface` and `probe.surface` are kept as parameters so nothing that
    // calls this has to change; both now describe a sea that lives one pass
    // later.
    void drawSurface;

    scene.setPipeline(this.meshPipe);
    scene.setBindGroup(0, this.globalsBind);
    for (const [name, slot] of this.slots) {
      if (!slot.count || slot.translucent || !this.probe.instances) continue;
      this.noteSlot(name, slot);
      scene.setBindGroup(1, slot.bind);
      scene.setVertexBuffer(0, slot.mesh.vbo);
      scene.setIndexBuffer(slot.mesh.ibo, 'uint32');
      scene.drawIndexed(slot.mesh.indexCount, slot.count);
    }

    // --- and here the frame splits, if anything is going to refract ---------
    //
    // Everything above is opaque and is now in the backdrop. Everything below
    // is translucent and may sample it.
    let pass = scene;
    if (refract) {
      scene.end();
      // Without MSAA there was no resolve to hijack, so the copy is explicit.
      // Only texels a visible refractor can sample are copied: its conservative
      // projected bounds, expanded by the shader's maximum displacement and a
      // linear-filter guard. Disconnected animals stay disconnected regions,
      // capped by area-minimising merges so command overhead cannot explode.
      if (!msaa) {
        const copied = this.tsRegion(enc, 'backdrop');
        for (const r of regions) {
          enc.copyTextureToTexture(
            { texture: this.hdr, origin: { x: r.x0, y: r.y0 } },
            { texture: this.backdrop, origin: { x: r.x0, y: r.y0 } },
            { width: r.x1 - r.x0, height: r.y1 - r.y0 },
          );
        }
        copied();
      }
      pass = enc.beginRenderPass({
        label: 'scene:translucent',
        timestampWrites: this.tsPass('scene:translucent'),
        colorAttachments: [{
          view: this.vHdrMS,
          ...(msaa ? { resolveTarget: this.vHdr } : {}),
          loadOp: 'load',
          storeOp: (msaa ? 'discard' : 'store') as GPUStoreOp,
        }, ...(this.taa ? [{
          view: this.vMotion,
          loadOp: 'load' as const,
          storeOp: 'store' as const,
        }] : []), {
          view: this.vMrfMS,
          ...(msaa ? { resolveTarget: this.vMrf } : {}),
          loadOp: 'load' as const,
          storeOp: (msaa ? 'discard' : 'store') as GPUStoreOp,
        }],
        depthStencilAttachment: {
          view: this.vDepth,
          depthLoadOp: 'load',
          depthStoreOp: 'store',
        },
      });
    }

    pass.setBindGroup(0, this.globalsBind);
    // Group 2 is the backdrop. Bound whether or not the split happened — with
    // no split it holds LAST frame's opaque scene, which is wrong by one frame
    // and would smear; `U.tuneD.y` is 0 in that case and the shader adds
    // nothing, so it is never read. The binding still has to exist because the
    // pipeline layout demands it.
    pass.setBindGroup(2, this.backdropBind);
    /**
     * GLASS BEFORE TISSUE, and the order is a choice rather than an accident.
     *
     * Neither writes depth, so between themselves the later draw simply blends
     * on top. Glass is architecture bolted to solid geometry; jellyfish drift
     * in open water. Drawing the panes first means an animal passing a window
     * blends over it, which is what happens; the case it gets wrong is a
     * jellyfish INSIDE the base, behind the glass, and nothing in the game puts
     * one there.
     *
     * Two loops rather than a sort, because there are two pipelines and
     * interleaving them would rebind on every slot for an ordering neither
     * needs.
     */
    const drawTranslucent = (wantGlass: boolean) => {
      for (const [name, slot] of this.slots) {
        if (!slot.count || !slot.translucent || !this.probe.instances) continue;
        if (slot.glass !== wantGlass) continue;
        this.noteSlot(name, slot);
        pass.setBindGroup(1, slot.bind);
        pass.setVertexBuffer(0, slot.mesh.vbo);
        pass.setIndexBuffer(slot.mesh.ibo, 'uint32');
        pass.drawIndexed(slot.mesh.indexCount, slot.count);
      }
    };
    pass.setPipeline(this.glassPipe);
    drawTranslucent(true);
    pass.setPipeline(this.jellyPipe);
    drawTranslucent(false);

    this.stats.particles = this.probe.particles ? this.particles.total : 0;
    this.stats.lights = this.lightCount;
    if (this.probe.particles) {
      pass.setPipeline(this.probe.moteGbuf ? this.particlePipeAux : this.particlePipe);
      pass.setBindGroup(0, this.globalsBind);
      pass.setBindGroup(1, this.particleBind);
      pass.draw(6, this.particles.total);
    }

    pass.end();

    // The cloud bake used to run here. Gone with the volume march it fed: the
    // deck in `skyColour` is one noise sample per sky pixel, so there is
    // nothing left to precompute and nothing left to go stale.

    // --- bloom ------------------------------------------------------------
    const blit = (
      pipeline: GPURenderPipeline,
      bind0: GPUBindGroup,
      bind1: GPUBindGroup | null,
      target: GPUTextureView,
      label: string,
      /** Restrict the draw to these target pixels: [x, y, w, h]. */
      scissor?: [number, number, number, number],
    ) => {
      const pass = enc.beginRenderPass({
        label,
        colorAttachments: [{ view: target, clearValue: { r: 0, g: 0, b: 0, a: 1 }, loadOp: 'clear', storeOp: 'store' }],
        // Every full-screen pass in the chain is a `blit`, so timing all of
        // them costs one line here rather than one per call site.
        timestampWrites: this.tsPass(label),
      });
      pass.setPipeline(pipeline);
      pass.setBindGroup(0, bind0);
      if (bind1) pass.setBindGroup(1, bind1);
      // THE CLEAR STILL COVERS THE WHOLE TARGET. A load operation is not a
      // draw and the scissor does not gate it, which is the entire reason this
      // works: outside the rect the texture is zeroed rather than left stale,
      // so a masked bloom chain contributes exactly nothing there instead of
      // contributing last frame.
      if (scissor) pass.setScissorRect(scissor[0], scissor[1], scissor[2], scissor[3]);
      pass.draw(3);
      pass.end();
    };

    // THE CAUSTIC MAP, before anything reads it.
    //
    // Cleared every frame rather than accumulated: the splat is a deterministic
    // function of the wave field, so it is already as stable as the water is,
    // and a history would smear the pattern across the boat's own motion. It is
    // one draw of a quarter-million points into 256 x 256 — against a frame of
    // 2.4 million pixels, which is what makes doing this properly affordable at
    // all.
    if (this.probe.caustics && this.gfxSky > 0 && this.seaTune.caustic > 0.001) {
      const cp = enc.beginRenderPass({
        label: 'caustic',
        timestampWrites: this.tsPass('caustic'),
        colorAttachments: [{
          view: this.vCaustic, loadOp: 'clear', storeOp: 'store',
          clearValue: { r: 0, g: 0, b: 0, a: 1 },
        }],
      });
      cp.setPipeline(this.causticPipe);
      cp.setBindGroup(0, this.causticBind);
      cp.draw(CAUSTIC_POINTS);
      cp.end();
    } else {
      // Wiped, not left stale — a switched-off effect that keeps its last frame
      // painted on the seabed is worse than one that is simply absent.
      const cp = enc.beginRenderPass({
        label: 'caustic:clear',
        colorAttachments: [{
          view: this.vCaustic, loadOp: 'clear', storeOp: 'store',
          clearValue: { r: 0, g: 0, b: 0, a: 1 },
        }],
      });
      cp.end();
    }

    // Inscatter first: the composite reads it, and at half resolution it is a
    // quarter of the fragments the old in-line march cost.
    blit(this.volPipe, this.volBind, null, this.vVol, 'volumetric');

    // Defocus. Skipped outright when off — the composite's own branch would
    // still leave the pass on the bill.
    if (this.dof > 0) blit(this.dofPipe, this.dofBind, null, this.vDofB, 'dof');

    if (p.bloom > 0 && this.probe.bloom) {
      // ON EVERY PASS OF THE CHAIN, NOT ONLY THE EXTRACT.
      //
      // Scissoring the extract alone stops the station getting IN, because
      // nothing outside the rect is ever written as a bright pixel. It does not
      // stop the panel's own glow getting OUT: the blur that follows reads
      // inside the rect and writes wherever its kernel reaches, so a bright
      // crystal would spread its halo across the rows beside it.
      //
      // Scissoring the blurs as well clips the kernel at the border in both
      // directions. Inside, the taps that fall outside read the cleared zero,
      // which is correct — there is nothing out there to bloom. Outside, the
      // pixels are never written at all and stay zero, so the composite's
      // unconditional add contributes nothing and needs no rect of its own.
      const m = p.bloomRect
        ? maskRect(p.bloomRect, this.bloomW, this.bloomH) : undefined;
      blit(this.brightPipe, this.brightBind, null, this.vBloomA, 'bright', m);
      for (let i = 0; i < this.quality.blurPasses; i++) {
        const from = i % 2 === 0 ? 0 : 1;
        const target = i % 2 === 0 ? this.vBloomB : this.vBloomA;
        blit(this.blurPipe, this.blurBinds[from], this.blurUniformBinds[i], target, `blur${i}`, m);
      }
    }

    // --- composite --------------------------------------------------------
    const screenTex = this.gpu.context.getCurrentTexture();
    const screen = screenTex.createView();
    if (this.taa) {
      // Composite first, into an intermediate: the resolve has to read finished
      // pixels, and the swapchain cannot be read while it is being written.
      blit(this.compositePipe, this.compositeBind, null, this.vLdr, 'composite');
      const k = this.taaFlip;
      if (this.taaCold) {
        // No history on the first frame after a resize. Resolving against the
        // cleared texture would blend in one black frame, which reads as a
        // flash — so seed it with the frame itself and start accumulating next
        // time. A copy, not a pass: there is nothing to compute.
        enc.copyTextureToTexture(
          { texture: this.ldr! }, { texture: this.history[k]! },
          [this.gpu.width, this.gpu.height],
        );
        this.taaCold = false;
      } else {
        blit(this.taaPipe, this.taaBinds[k]!, null, this.vHistory[k]!, 'taa');
      }
      // The resolved frame is both the output and the next frame's history. It
      // used to be COPIED to the screen; it is sharpened on the way instead,
      // which costs a pass that was already being paid for as a copy and lands
      // the sharpen exactly where it belongs — after the resolve, which is the
      // thing that softened it.
      blit(this.sharpenPipe, this.sharpenBinds[k]!, null, screen, 'sharpen');
      this.taaFlip = 1 - k;
    } else if (this.fxaa) {
      blit(this.compositePipe, this.compositeBind, null, this.vLdr, 'composite');
      blit(this.fxaaPipe, this.fxaaBind, null, screen, 'fxaa');
    } else {
      blit(this.compositePipe, this.compositeBind, null, screen, 'composite');
    }

    // TEXT LAST, straight onto the swapchain.
    //
    // After the resolve and the sharpen rather than before them: a glyph put
    // through TAA reprojects against a motion vector it never wrote, and one
    // put through the sharpen gets a dark halo on every stroke. It is also why
    // this is the only pass in the frame that draws at native resolution — the
    // scene renders to a pixel budget, and a label should not.
    if (this.text.count > 0) {
      const tp = enc.beginRenderPass({
        label: 'text',
        timestampWrites: this.tsPass('text'),
        colorAttachments: [{ view: screen, loadOp: 'load', storeOp: 'store' }],
      });
      this.text.flush(tp);
      tp.end();
    }

    if (ts && this.tsResolve) {
      // NO CLOSING MARK, AND THAT IS MEASURED. A nop compute pass at the end of
      // the frame does not wait for the render passes before it: timing four
      // heavy render passes between two nop marks, the passes summed to 3.59 ms
      // while the marks were 0.14 ms apart, and the closing mark landed BEFORE
      // the first render pass had finished. Compute and render timestamps on
      // this driver are not ordered against each other.
      //
      // So the frame is the span from the opening mark to the last REAL pass,
      // which is the same thing as the sum of the per-pass costs. That makes
      // the total only as available as the breakdown is — see the readout, and
      // see `gpuPassTiming` for why that is a switch rather than always on.
      const slot = this.tsFlip;
      const read = this.tsRead[slot];
      if (read && !this.tsBusy[slot]) {
        // Only what was written. Resolving the whole set would hand back
        // whatever the unused tail holds, which on a frame that skipped bloom
        // is the previous frame's bloom.
        const used = this.tsUsed;
        enc.resolveQuerySet(ts, 0, used, this.tsResolve, 0);
        enc.copyBufferToBuffer(this.tsResolve, 0, read, 0, used * 8);
        this.tsBusy[slot] = true;
        // THE LABELS TRAVEL WITH THE BUFFER, not with the renderer. This lands
        // two frames from now, and by then `tsNames` describes a different
        // frame's pass list — one that may have gained a depth-of-field pass or
        // lost the whole bloom chain. Reading the live array would put the
        // right numbers under the wrong names, which is worse than no numbers.
        this.tsFlight[slot] = this.tsNames;
        this.tsFlip = (slot + 1) % this.tsRead.length;
        device.queue.submit([enc.finish()]);
        void read.mapAsync(GPUMapMode.READ).then(() => {
          const v = new BigUint64Array(read.getMappedRange().slice(0));
          read.unmap();
          this.tsBusy[slot] = false;
          const names = this.tsFlight[slot];
          this.tsFlight[slot] = null;
          // A TIMELINE, DIFFERENCED — not a set of independent pairs.
          //
          // Every pass writes ONE timestamp, at its end. A pass's cost is the
          // distance from the previous mark to its own, and the frame is the
          // whole span from the opening mark to the closing one.
          //
          // IT IS NOT `end - begin`, AND THAT IS MEASURED RATHER THAN ASSUMED.
          // Timing four render passes of 1x, 2x, 4x and 1x work on Metal via
          // Dawn, `end - begin` came back 1.17, 7.52, 14.09, 15.22 ms — rising
          // monotonically for work that falls at the end, which is the
          // signature of `beginningOfPassWriteIndex` being sampled when the
          // encoder is SCHEDULED rather than when the pass starts running. Each
          // pass was reporting the time since submit. Sorted by cost, that
          // ranks passes by the order they were encoded in and calls it a
          // profile.
          //
          // The gap between one pass ending and the next ending includes
          // whatever the driver did in between — a buffer copy, a barrier,
          // nothing at all — so that time is attributed to the pass that
          // follows it. That is the honest place for it: it is time the frame
          // spent, and no pass has a better claim on it.
          // The label list IS the length of the timeline: one entry per mark,
          // recorded when the marks were claimed. Without it there is no way to
          // know how many of the query set's 64 slots this frame filled.
          if (!names || names.length < 2) return;
          // The frame graph is dynamic: a frame with visible refraction has
          // scene:opaque/backdrop/scene:translucent, while a frame without it
          // has one scene pass. Do not leave pass timings from the previously
          // completed graph in the HUD or both paths appear to have run.
          const liveNames = new Set(names.filter((name) => name.length > 0));
          for (const name of Object.keys(this.gpuPass)) {
            if (!liveNames.has(name)) delete this.gpuPass[name];
          }
          const t0 = Number(v[0]!);
          const last = Number(v[names.length - 1]!);
          // The span from the opening mark to the last pass to finish. Equal by
          // construction to the sum of the deltas below, which is the honest
          // thing to call a frame: measured work, not a bracket that does not
          // wait for it.
          const dt = (last - t0) / 1e6;
          if (dt > 0 && dt < 500) { this.gpuMs += (dt - this.gpuMs) * 0.12; this.tsSamples++; }
          else if (this.tsSamples === 0 && ++this.tsDud === 120) {
            // Once, after two seconds of nothing. A silent zero is exactly how
            // this failed before: the feature is on, the passes are encoded, no
            // error is raised anywhere, and the number is simply never anything.
            console.warn('[abyss] timestamp-query is enabled but every frame reads 0 — ' +
                         'a pass that encodes no work writes no timestamp; see `tsNop`');
          }
          for (let i = 1; i < names.length; i++) {
            const name = names[i]!;
            // The empty label is a boundary mark: it advances the timeline and
            // is not a pass anybody can act on.
            if (!name) continue;
            const ms = (Number(v[i]!) - Number(v[i - 1]!)) / 1e6;
            if (!(ms >= 0 && ms < 500)) continue;
            const was = this.gpuPass[name] ?? ms;
            this.gpuPass[name] = was + (ms - was) * 0.12;
          }
        }).catch(() => { this.tsBusy[slot] = false; this.tsFlight[slot] = null; });
        this.publish();
        return;
      }
    }
    device.queue.submit([enc.finish()]);
    this.publish();
  }

  /** Copy the working counters out, now that the frame is fully encoded. */
  private publish() {
    const a = this.stats, b = this.published;
    b.terrainTris = a.terrainTris; b.terrainDraws = a.terrainDraws;
    b.instTris = a.instTris; b.instDraws = a.instDraws;
    b.particles = a.particles;
    b.slotOverflow = a.slotOverflow; b.lightOverflow = a.lightOverflow;
    b.lights = a.lights; b.lightCap = a.lightCap;
    b.backdropEnabled = a.backdropEnabled;
    b.backdropPixels = a.backdropPixels; b.backdropRegions = a.backdropRegions;
    b.backdropCoverage = a.backdropCoverage;
    // Copied, not aliased: `resetQueue` truncates the working array in place,
    // so a shared reference would empty the published one too and the slot
    // pressure line would blink out on the next frame.
    b.top.length = 0;
    for (const t of a.top) b.top.push(t);
  }
}
